import '../../../styles/om-module.css';
import '../../../styles/om-modals.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileWarning, Plus, Search, ChevronRight, Clock, CheckCircle2, XCircle,
  AlertTriangle, User, Calendar, Wrench, Shield, ClipboardList,
  Download, RefreshCw, Zap, Lock, ArrowLeft, X, Check, Bell,
  FileText, ChevronDown, ChevronUp, Activity, BarChart2, AlertCircle,
  Flame, TrendingUp, Wind, Shovel, HardHat, ToggleLeft,
  ToggleRight, LogOut, RotateCcw, Send, Users, Key, ThumbsUp,
  LayoutGrid, List, BookOpen, Info, Cpu, Thermometer, Eye,
  Radio, Database, Smartphone, GitBranch, CheckSquare, Clipboard,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteDisplayName } from '../../../utils/siteUtils';
import {
  ptwApi, ptwKPIApi, ptwNotificationsApi,
} from '../../../services/ptwApi';
import backendApiService from '../../../services/backendApi';

// ─── Abbreviations Glossary ───────────────────────────────────────────────────
const ABBREVIATIONS: Record<string, string> = {
  COMO: 'Chief O&M Officer',
  HIRA: 'Hazard Identification and Risk Assessment',
  'HSS&E': 'Health Safety Security and Environment',
  IA: 'Isolation Authority',
  IC: 'Safety Document (Isolation Certificate)',
  IDLH: 'Immediately Dangerous to Life or Health',
  JHA: 'Job Hazard Analysis',
  KKS: 'Equipment Numbering & Identification System',
  LEL: 'Lower Explosive Limit',
  MOC: 'Management of Change',
  PI: 'Permit Issuer',
  PR: 'Permit Receiver',
  PTW: 'Permit to Work',
  SC: 'Safety Controller',
  SCr: 'Safety Coordinator',
  SP: 'Skilled Person',
  SRCC: 'Safety Rules Clearance Certificate',
  SREC: 'Safety Rules Exclusion Certificate',
  SS: 'Shift Supervisor',
  STEL: 'Short Term Exposure Limit',
  TBT: 'Toolbox Talk',
  TWA: 'Time Weighted Average',
  WBGT: 'Wet Bulb Globe Temperature',
  WO: 'Work Order',
};

// ─── 10-Step Workflow Config ──────────────────────────────────────────────────
const WORKFLOW_STEPS_CONFIG = [
  {
    id: 1, title: 'Work Order Generation', type: 'auto' as const,
    desc: 'SCADA/CMMS raises WO automatically on alarm/threshold or site team submits via mobile app. Contains equipment ID, fault/defect description, timestamp, priority (P1/P2/P3).',
    icon: <Database size={14} />, target: 'Instant',
  },
  {
    id: 2, title: 'Work Type Classification', type: 'auto' as const,
    desc: 'Classification engine reads fault code and asset master. Assigns: PREVENTIVE / BREAKDOWN / CORRECTIVE. P1 = Breakdown, P2 = Corrective, P3 = Preventive.',
    icon: <GitBranch size={14} />, target: '< 2 sec',
  },
  {
    id: 3, title: 'PTW Type Selection', type: 'auto' as const,
    desc: 'Rule engine maps work type + equipment category to PTW type: Main PTW, PTW (LIVE), PTW (ACCESS), PTW (TEST). Supplementary permits auto-flagged.',
    icon: <Shield size={14} />, target: '< 2 sec',
  },
  {
    id: 4, title: 'JHA Auto-Population', type: 'auto' as const,
    desc: 'System loads pre-approved JHA template from library based on work type + equipment tag. All hazards, controls and risk ratings pre-filled. SP reviews in < 2 min.',
    icon: <ClipboardList size={14} />, target: '< 2 min (SP review)',
  },
  {
    id: 5, title: 'PPE & Tools Auto-Selection', type: 'auto' as const,
    desc: 'PPE and tool checklists auto-generated from JHA hazard codes. Site technician confirms availability via mobile app checkbox.',
    icon: <HardHat size={14} />, target: 'Instant',
  },
  {
    id: 6, title: 'Isolation Schedule Generation', type: 'auto' as const,
    desc: 'Isolation schedule auto-populated from SLD database using equipment KKS tag. Lists all POIs (breakers, isolators, disconnects) with KKS numbers, lock/tag requirements.',
    icon: <Lock size={14} />, target: 'Instant',
  },
  {
    id: 7, title: 'SC Review & Approve', type: 'human' as const,
    desc: 'Safety Controller receives PTW package on dashboard. Reviews JHA, isolation schedule, supplementary permits. One-click "Prepared" digital signature. System rejects incomplete packages.',
    icon: <Eye size={14} />, target: '< 5 min',
  },
  {
    id: 8, title: 'PTW Issuance to PR', type: 'auto' as const,
    desc: 'System issues PTW to Permit Receiver via mobile app notification. PR signs digitally. Key-safe QR activated. LAR updated. Isolation keys unlocked from e-key safe.',
    icon: <Smartphone size={14} />, target: '< 3 min (PI action)',
  },
  {
    id: 9, title: 'Work Execution & Monitoring', type: 'field' as const,
    desc: 'IA performs isolation per schedule (confirmed via mobile scan of lock QR codes). PR conducts TBT (digital sign-on). Work executed per JHA. Real-time status on control room dashboard.',
    icon: <Radio size={14} />, target: 'Per work scope',
  },
  {
    id: 10, title: 'Closure & Plant Restoration', type: 'human' as const,
    desc: 'PR clears PTW digitally. SC verifies all locks removed, issues de-isolation instruction. CMMS WO automatically closed. Asset history updated. KPI data captured.',
    icon: <CheckSquare size={14} />, target: '< 5 min (SC action)',
  },
];

// ─── Auto-Classification Matrix ───────────────────────────────────────────────
// [wo_type][equipment_category] → { permitType, note, safetyDoc }
const AUTO_CLASSIFICATION: Array<{
  wo: string[]; equip: string; isolate: boolean; live: boolean; hv: boolean;
  permitType: string; note: string;
}> = [
  { wo: ['PREVENTIVE'], equip: 'DC String / Combiner (isolated)', isolate: true, live: false, hv: false, permitType: 'MAIN', note: 'Standard Main PTW' },
  { wo: ['PREVENTIVE'], equip: 'AC LV Panel / MCC (isolated)', isolate: true, live: false, hv: false, permitType: 'MAIN', note: 'Standard Main PTW' },
  { wo: ['PREVENTIVE'], equip: 'HV Transformer / Switchgear', isolate: true, live: false, hv: true, permitType: 'MAIN', note: 'Main PTW + Safety Document mandatory' },
  { wo: ['PREVENTIVE'], equip: 'Tracker / Mechanical', isolate: true, live: false, hv: false, permitType: 'MAIN', note: 'Main PTW' },
  { wo: ['PREVENTIVE'], equip: 'Monitoring / SCADA (control)', isolate: false, live: false, hv: false, permitType: 'LAR', note: 'Limited Access Request only' },
  { wo: ['BREAKDOWN'], equip: 'DC String (fault, live test)', isolate: false, live: true, hv: false, permitType: 'LIVE', note: 'PTW (LIVE) — DC LV live work' },
  { wo: ['BREAKDOWN'], equip: 'Inverter HV AC output fault', isolate: true, live: false, hv: true, permitType: 'MAIN', note: 'Main PTW + Safety Document' },
  { wo: ['BREAKDOWN'], equip: 'HV Cable fault', isolate: true, live: false, hv: true, permitType: 'MAIN', note: 'Main PTW + Safety Document' },
  { wo: ['BREAKDOWN'], equip: 'HV Switchgear CB fault', isolate: true, live: false, hv: true, permitType: 'TEST', note: 'PTW (TEST) / PTW (ROSH)' },
  { wo: ['CORRECTIVE'], equip: 'Module replacement', isolate: true, live: false, hv: false, permitType: 'MAIN', note: 'Main PTW — string isolation required' },
  { wo: ['CORRECTIVE'], equip: 'LV Cable re-route (live adj.)', isolate: true, live: false, hv: true, permitType: 'ACCESS', note: 'Main PTW (ACCESS)' },
  { wo: ['CORRECTIVE'], equip: 'Protection relay re-setting', isolate: false, live: true, hv: true, permitType: 'TEST', note: 'PTW (TEST) — CB remains, live work' },
  { wo: ['CORRECTIVE'], equip: 'Earthing system modification', isolate: true, live: false, hv: true, permitType: 'MAIN', note: 'Main PTW + Safety Document' },
];

// ─── Supplementary Permit Config ─────────────────────────────────────────────
const SUPP_PERMIT_CONFIG: Record<string, {
  label: string; icon: any; color: string; formRef: string;
  trigger: string; requirements: string[];
}> = {
  HOT_WORK: {
    label: 'Hot Work Permit',
    icon: Flame,
    color: '#1C4D88',
    formRef: 'NC/HSE/SP-040/FM-031',
    trigger: 'Welding, grinding, cutting, open flame. Equipment in Ex-zone.',
    requirements: ['Fire watch assigned', 'Gas test pre-job', '60-min post-job watch', '35 ft clearance from combustibles'],
  },
  WORK_AT_HEIGHT: {
    label: 'Work at Height Permit',
    icon: TrendingUp,
    color: '#0084CF',
    formRef: 'NC/HSE/SP-040/FM-033',
    trigger: 'Work above 1.8m. Module replacement on elevated racks, tracker work, elevated junction box.',
    requirements: ['Harness mandatory', 'Scaffold inspection cert required', 'No work in high wind > 30 km/h'],
  },
  LIFTING: {
    label: 'Lifting Operations Permit',
    icon: Wind,
    color: '#0066A3',
    formRef: 'NC/HSE/SP-040/FM-036',
    trigger: 'Crane or hoist used. Transformer replacement, inverter exchange, large equipment movement.',
    requirements: ['Rigging plan approved', 'SWL check confirmed', 'Wind speed < 9.8 m/s (mobile crane)', 'Exclusion zone barricaded'],
  },
  EXCAVATION: {
    label: 'Excavation Permit',
    icon: Shovel,
    color: '#0066A3',
    formRef: 'NC/HSE/SP-040/FM-006',
    trigger: 'Excavation > 0.5m. Cable fault location, earthing electrode installation, drainage work.',
    requirements: ['Underground drawings consulted', 'Soft-dig first 0.5m', 'Shoring for > 1.2m depth', 'Atmospheric test for > 1.2m depth'],
  },
  CONFINED_SPACE: {
    label: 'Confined Space Permit',
    icon: Lock,
    color: '#0084CF',
    formRef: 'NC/HSE/SP-040/FM-032',
    trigger: 'Cable tunnel, battery room, vault, manhole, duct bank access pit entry.',
    requirements: [
      'Gas test: O₂ 19.5–23.5%, LEL < 10%, CO < 35 ppm',
      'Standby person at surface',
      'SCBA on standby',
      'Continuous atmospheric monitoring',
    ],
  },
};

// ─── PPE Master List (auto-selection) ────────────────────────────────────────
const PPE_MASTER: Array<{
  item: string; standard: string; mandatory: boolean; hazardFlag: string; autoIf: string;
}> = [
  { item: 'Class E Safety Helmet', standard: 'ANSI Z89.1 / EN397', mandatory: true, hazardFlag: 'GENERAL', autoIf: 'Always' },
  { item: 'Safety Glasses (Clear/Tinted)', standard: 'EN166 / ANSI Z87.1', mandatory: true, hazardFlag: 'GENERAL', autoIf: 'Always' },
  { item: 'Hi-Vis Vest (Class 2 min.)', standard: 'EN ISO 20471', mandatory: true, hazardFlag: 'GENERAL', autoIf: 'Always' },
  { item: 'Safety Boots (Steel toe, antistatic)', standard: 'EN ISO 20345 S1P/S3', mandatory: true, hazardFlag: 'GENERAL', autoIf: 'Always' },
  { item: 'Electrical Insulating Gloves (Cat IV)', standard: 'EN 60903 / ASTM D120', mandatory: false, hazardFlag: 'ELEC-HV', autoIf: 'HV or LV electrical work' },
  { item: 'Arc Flash Face Shield (40 cal/cm²)', standard: 'NFPA 70E HRC 2', mandatory: false, hazardFlag: 'ELEC-ARC', autoIf: 'Live work or PTW(LIVE)' },
  { item: 'FR Coverall (Category 2)', standard: 'EN ISO 11612 A1B1C1', mandatory: false, hazardFlag: 'ELEC-FIRE', autoIf: 'Arc flash or hot work' },
  { item: 'Full Body Safety Harness (Class A)', standard: 'EN 361 / ANSI Z359', mandatory: false, hazardFlag: 'WAH', autoIf: 'Work at Height > 1.8m' },
  { item: 'Cut-Resistant Gloves (Level D)', standard: 'EN 388:2016 Level 4341D', mandatory: false, hazardFlag: 'CUT', autoIf: 'Module or metalwork handling' },
  { item: 'Chemical-Resistant Gloves (Nitrile)', standard: 'EN 374-2', mandatory: false, hazardFlag: 'CHEM', autoIf: 'Battery or BESS work' },
  { item: 'P3 FFP3 Dust Mask', standard: 'EN 149:2001+A1:2009', mandatory: false, hazardFlag: 'DUST', autoIf: 'Filter replacement or dusty environment' },
  { item: 'Hearing Protection (SNR 30)', standard: 'EN 352-1', mandatory: false, hazardFlag: 'NOISE', autoIf: 'Noise > 85 dB in JHA' },
  { item: 'UV Protection Sunscreen SPF50+', standard: 'Site HSE requirement', mandatory: false, hazardFlag: 'ENVIRO', autoIf: 'Always outdoor work' },
];

// ─── Tools Master List ────────────────────────────────────────────────────────
const TOOLS_MASTER: Array<{
  tool: string; spec: string; applicable: string; calibRequired: boolean; autoIf: string;
}> = [
  { tool: 'Approved DC Voltmeter (1000V CAT IV)', spec: 'Fluke T6-1000 or equivalent', applicable: 'All DC work', calibRequired: true, autoIf: 'Any DC string work — 12 monthly cal.' },
  { tool: 'Approved HV Detector / Voltage Probe', spec: 'Salisbury 15kV rated', applicable: 'HV isolation confirm', calibRequired: true, autoIf: 'HV PTW raised — 6 monthly cal.' },
  { tool: 'Insulated Tool Set (1000V rated)', spec: 'IEC 60900 compliant', applicable: 'All LV/HV electrical', calibRequired: true, autoIf: 'Any electrical work — annual cal.' },
  { tool: 'Isolating Lock Set (unique keys)', spec: 'Site lock box + DNO tags', applicable: 'All isolation', calibRequired: false, autoIf: 'Always' },
  { tool: 'Multi-gas Detector (O₂/CO/H₂S/LEL)', spec: 'BW Clip4 or equivalent', applicable: 'Confined space/trench', calibRequired: true, autoIf: 'Confined space flag — daily bump test' },
  { tool: 'IR Thermometer / Thermal Camera', spec: 'FLIR E6 or equivalent', applicable: 'Thermographic inspection', calibRequired: true, autoIf: 'Preventive PM — annual cal.' },
  { tool: 'Insulation Resistance Tester (Megger)', spec: 'MIT515 or equivalent', applicable: 'Post-repair testing', calibRequired: true, autoIf: 'Any PTW(TEST) — annual cal.' },
  { tool: 'MC4 Disconnect Tool (non-contact)', spec: 'Staubli MC4 tool', applicable: 'Module DC connections', calibRequired: false, autoIf: 'Module replacement' },
  { tool: 'Scaffolding Inspection Tag', spec: 'Green/Red Tag system', applicable: 'Work at height', calibRequired: false, autoIf: 'WAH permit flagged' },
  { tool: 'First Aid Kit (Class C)', spec: 'BS 8599-1:2019', applicable: 'All work', calibRequired: false, autoIf: 'Always — check monthly' },
  { tool: 'CO₂ Fire Extinguisher (5kg)', spec: 'BS EN 3-7', applicable: 'All electrical work', calibRequired: false, autoIf: 'Always electrical — annual service' },
  { tool: 'Torque Wrench (calibrated)', spec: '0–150 Nm range', applicable: 'Module/inverter bolts', calibRequired: true, autoIf: 'Module/inverter work — annual cal.' },
  { tool: 'Portable Earthing Set (HV)', spec: '65mm² Cu, rated 16kA', applicable: 'HV cable work', calibRequired: true, autoIf: 'HV cable PTW — quarterly inspection' },
  { tool: 'Safety Barrier Tape & Cones (min 10m)', spec: 'Red/Yellow HSE tape', applicable: 'All PTW', calibRequired: false, autoIf: 'Always' },
];

const PERMIT_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  MAIN: { label: 'Main PTW', color: '#1C4D88', bg: 'bg-[#E3F0F9] text-[#1C4D88]' },
  LIVE: { label: 'PTW (Live)', color: '#dc2626', bg: 'bg-red-100 text-red-800' },
  ACCESS: { label: 'PTW (Access)', color: '#1C4D88', bg: 'bg-[#E3F0F9] text-[#1C4D88]' },
  TEST: { label: 'PTW (Test)', color: '#d97706', bg: 'bg-amber-100 text-amber-800' },
  WORKING_SPACE: { label: 'PTW (Working Space)', color: '#059669', bg: 'bg-emerald-100 text-emerald-800' },
};

const STATUS_CONFIG: Record<string, { label: string; dot: string; badge: string }> = {
  draft: { label: 'Draft', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700 border-gray-300' },
  jha_review: { label: 'JHA Review', dot: 'bg-[#3EA3DA]', badge: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  prepared: { label: 'Prepared', dot: 'bg-[#3EA3DA]', badge: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  issued: { label: 'Issued', dot: 'bg-[#3EA3DA]', badge: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  tbt_pending: { label: 'TBT Pending', dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  active: { label: 'Active', dot: 'bg-green-500', badge: 'bg-green-100 text-green-800 border-green-300' },
  suspended: { label: 'Suspended', dot: 'bg-orange-400', badge: 'bg-orange-100 text-orange-800 border-orange-300' },
  surrendered: { label: 'Surrendered', dot: 'bg-[#3EA3DA]', badge: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  completed: { label: 'Completed', dot: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700 border-slate-300' },
  cancelled: { label: 'Cancelled', dot: 'bg-red-400', badge: 'bg-red-100 text-red-800 border-red-300' },
};

const WORK_TYPE_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  electrical: { label: 'Electrical', icon: Zap, color: '#0084CF' },
  mechanical: { label: 'Mechanical', icon: Wrench, color: '#0084CF' },
  civil: { label: 'Civil', icon: Shovel, color: '#0084CF' },
  'hot-work': { label: 'Hot Work', icon: Flame, color: '#0084CF' },
  height: { label: 'Work at Height', icon: TrendingUp, color: '#0084CF' },
  'confined-space': { label: 'Confined Space', icon: Lock, color: '#0084CF' },
  general: { label: 'General', icon: ClipboardList, color: '#0084CF' },
};

const RISK_COLORS: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700 border-red-300',
  MEDIUM: 'bg-amber-100 text-amber-700 border-amber-300',
  LOW: 'bg-green-100 text-green-700 border-green-300',
};

interface PTWPermit {
  id: number;
  permit_no: string;
  jha_ref_no: string;
  permit_type: string;
  wo_number: string;
  wo_type: string;
  equipment_kks: string;
  work_description: string;
  work_type: string;
  is_hv_involved: boolean;
  status: string;
  planned_start: string;
  planned_end: string;
  validity_hours: number;
  key_safe_number: string;
  has_hot_work: boolean;
  has_work_at_height: boolean;
  has_lifting: boolean;
  has_excavation: boolean;
  has_confined_space: boolean;
  sc_name: string;
  pi_name: string;
  pr_name: string;
  ia_name?: string;
  sp_name?: string;
  safety_controller_id?: number;
  permit_issuer_id?: number;
  permit_receiver_id?: number;
  isolation_authority_id?: number;
  skilled_person_id?: number;
  created_at: string;
  issued_at: string;
  tbt_conducted_at?: string;
  work_started_at: string;
  sup_permit_types: string[];
  jha_items?: any[];
  ppe_items?: any[];
  tool_items?: any[];
  isolation_items?: any[];
  supplementary_permits?: any[];
  signatures?: any[];
  tbt_records?: any[];
  audit_logs?: any[];
  vendor_company_name?: string;
  vendor_person_name?: string;
  contractor_company?: string;
  supervisor_in_charge?: string;
  no_of_workers?: number;
  plant?: string;
  building?: string;
  department?: string;
  zone_area?: string;
  floor_line?: string;
  risk_severity?: string;
  risk_probability?: string;
  risk_category?: string;
  is_same_day?: boolean;
  check_in_time?: string;
  check_out_time?: string;
  query_notes?: string;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`omx-status ${cfg.badge}`}>
      <span className={`omx-status__dot ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const RiskBadge: React.FC<{ risk: string }> = ({ risk }) => (
  <span className={`omx-risk ${RISK_COLORS[risk] || RISK_COLORS.LOW}`}>{risk}</span>
);

// ─── Current User ─────────────────────────────────────────────────────────────
interface CurrentUser {
  id: number;
  name: string;
  username: string;
  role: string; // system role: admin | manager | engineer | technician | viewer
}

// Badge config for each permit-level role
const ROLE_ON_PERMIT_LABELS: Record<string, { label: string; badgeCls: string }> = {
  SC:     { label: 'Safety Controller',   badgeCls: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  PI:     { label: 'Permit Issuer',        badgeCls: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
  PR:     { label: 'Permit Receiver',      badgeCls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  SP:     { label: 'Skilled Person',       badgeCls: 'bg-amber-100 text-amber-800 border-amber-300' },
  IA:     { label: 'Isolation Authority',  badgeCls: 'bg-red-100 text-red-800 border-red-300' },
  ADMIN:  { label: 'Admin / Manager',      badgeCls: 'bg-gray-100 text-gray-800 border-gray-300' },
  VIEWER: { label: 'View Only',            badgeCls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/** Determine which role (if any) the logged-in user holds on a specific permit. */
const getMyRoleOnPermit = (permit: PTWPermit, cu: CurrentUser | null): string => {
  if (!cu) return 'VIEWER';
  const sysRole = (cu.role || '').toLowerCase();
  if (sysRole === 'admin' || sysRole === 'manager' || sysRole === 'superadmin') return 'ADMIN';
  if (permit.safety_controller_id === cu.id)  return 'SC';
  if (permit.permit_issuer_id === cu.id)       return 'PI';
  if (permit.permit_receiver_id === cu.id)     return 'PR';
  if (permit.skilled_person_id === cu.id)      return 'SP';
  if (permit.isolation_authority_id === cu.id) return 'IA';
  return 'VIEWER';
};

/** Map [role][status] → list of action keys the role can perform */
const ROLE_STATUS_ACTIONS: Record<string, Record<string, string[]>> = {
  SC:    {
    draft:      ['submitForReview', 'cancel'],
    prepared:   ['scPrepare', 'cancel'],
    active:     ['suspend'],
    suspended:  ['resume'],
    surrendered:['close'],
  },
  PI:    {
    issued:     ['piIssue', 'cancel'],
    active:     ['suspend'],
    suspended:  ['resume'],
  },
  PR:    {
    tbt_pending: ['conductTBT'],
    active:      ['surrender'],
  },
  SP:    {
    jha_review: ['spApproveJHA'],
  },
  IA:    {},
  ADMIN: {
    draft:       ['submitForReview', 'cancel'],
    jha_review:  ['spApproveJHA', 'cancel'],
    prepared:    ['scPrepare', 'cancel'],
    issued:      ['piIssue', 'cancel'],
    tbt_pending: ['conductTBT', 'cancel'],
    active:      ['suspend', 'surrender', 'cancel'],
    suspended:   ['resume', 'cancel'],
    surrendered: ['close', 'cancel'],
  },
  VIEWER: {},
};

/** Guidance: who needs to act next at each status */
const STATUS_NEXT_ACTION: Record<string, { message: string; actor: string; urgent?: boolean }> = {
  draft:       { message: 'Awaiting submission for JHA review',           actor: 'SC' },
  jha_review:  { message: 'SP must review and approve the JHA',            actor: 'SP', urgent: true },
  prepared:    { message: 'SC must review, then PI issues the permit',      actor: 'SC → PI', urgent: true },
  issued:      { message: 'PI must formally issue permit to PR',            actor: 'PI', urgent: true },
  tbt_pending: { message: 'PR must conduct Toolbox Talk before work starts', actor: 'PR', urgent: true },
  active:      { message: 'Work in progress — PR surrenders on completion', actor: 'PR' },
  suspended:   { message: 'Work suspended — SC/PI to resume when safe',     actor: 'SC/PI', urgent: true },
  surrendered: { message: 'SC must verify isolation removed and close permit', actor: 'SC', urgent: true },
  completed:   { message: 'Permit closed. CMMS WO auto-updated.',           actor: '—' },
  cancelled:   { message: 'Permit cancelled.',                              actor: '—' },
};

// ─── Reason Modal (replaces browser prompt) ───────────────────────────────────
const ReasonModal: React.FC<{
  title: string; placeholder: string; loading?: boolean;
  confirmLabel?: string; dangerMode?: boolean;
  onConfirm: (reason: string) => void; onCancel: () => void;
}> = ({ title, placeholder, loading, confirmLabel = 'Confirm', dangerMode = false, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="omx omx-overlay" style={{ zIndex: 200000 }} onClick={onCancel}>
      <motion.div
        initial={{ scale: 0.97, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.97, opacity: 0, y: 10 }}
        className="omx-modal omx-modal--narrow"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={e => e.stopPropagation()}
      >
        <div className="omx-modal__head">
          <div style={{ minWidth: 0 }}>
            <span className="omx-modal__eyebrow">Permit action</span>
            <div className="omx-modal__title">{title}</div>
          </div>
          <button onClick={onCancel} className="omx-modal__close" aria-label="Close">
            <X size={17} strokeWidth={1.9} />
          </button>
        </div>

        <div className="omx-modal__body">
          {dangerMode && (
            <div className="omx-notice omx-notice--err" style={{ marginBottom: 14 }}>
              <AlertTriangle size={15} strokeWidth={1.9} />
              <span>This action cannot be undone. The reason you enter is recorded on the permit.</span>
            </div>
          )}
          <label htmlFor="ptw-reason">Reason</label>
          <textarea
            id="ptw-reason"
            rows={4}
            placeholder={placeholder}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>

        <div className="omx-modal__foot">
          <button onClick={onCancel} className="omx-btn">Cancel</button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={!reason.trim() || loading}
            className="omx-btn omx-btn--primary"
            style={dangerMode ? { background: '#B3261E', borderColor: '#B3261E' } : undefined}
          >
            {loading && <RefreshCw size={13} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── User Picker (for wizard role assignments) ────────────────────────────────
const UserPicker: React.FC<{
  label: string; desc: string; required?: boolean;
  value: number | ''; selectedName: string;
  users: Array<{ id: number; full_name?: string; username: string; role?: string }>;
  onChange: (id: number | '', name: string) => void;
}> = ({ label, desc, required, value, selectedName, users, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = users.filter(u =>
    u.role !== 'system_admin' && u.role !== 'super_admin' && u.username !== 'Develop' &&
    (u.full_name || u.username).toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="relative">
      <span className="omx-field-label">
        {label}{required && <span className="omx-req">*</span>}
      </span>
      <button type="button" onClick={() => setOpen(!open)} className="omx-select" aria-haspopup="listbox" aria-expanded={open}>
        <span className={value ? 'omx-select__value' : 'omx-select__ph'}>{value ? selectedName : 'Select user…'}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[1000]" onClick={() => setOpen(false)} />
          <div className="omx-menu" role="listbox">
            <div className="omx-menu__search">
              <Search size={13} />
              <input autoFocus type="text" placeholder="Search name or username…" value={search}
                onChange={e => setSearch(e.target.value)} aria-label="Search users" />
            </div>
            <div className="omx-menu__list">
              <button type="button" onClick={() => { onChange('', ''); setOpen(false); }} className="omx-menu__item omx-menu__item--clear">
                Clear selection
              </button>
              {filtered.length === 0 && <p className="omx-menu__empty">No users found</p>}
              {filtered.map(u => (
                <button key={u.id} type="button" role="option" aria-selected={u.id === value}
                  onClick={() => { onChange(u.id, u.full_name || u.username); setOpen(false); setSearch(''); }}
                  className={`omx-menu__item${u.id === value ? ' is-selected' : ''}`}>
                  <span className="omx-menu__avatar"><User size={11} /></span>
                  <span className="omx-menu__body">
                    <span className="omx-menu__name">{u.full_name || u.username}</span>
                    <span className="omx-menu__sub">@{u.username}{u.role ? ` \u00b7 ${u.role}` : ''}</span>
                  </span>
                  {u.id === value && <Check size={13} className="flex-shrink-0" style={{ color: 'var(--om-blue)' }} />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <p className="omx-field-help">{desc}</p>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }> = ({
  title, icon, children, defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="omx-card mb-4">
      <button type="button" onClick={() => setOpen(!open)} className="omx-card__head w-full" aria-expanded={open}
        style={{ cursor: 'pointer', borderBottomWidth: open ? 1 : 0 }}>
        <span className="omx-card__title">{icon} {title}</span>
        {open ? <ChevronUp size={15} style={{ color: 'var(--om-subtle)' }} /> : <ChevronDown size={15} style={{ color: 'var(--om-subtle)' }} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="omx-card__body">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Abbreviation Tooltip ─────────────────────────────────────────────────────
const AbbrevTooltip: React.FC<{ abbr: string }> = ({ abbr }) => {
  const [show, setShow] = useState(false);
  const full = ABBREVIATIONS[abbr];
  if (!full) return <span className="font-bold">{abbr}</span>;
  return (
    <span className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span className="omx-abbr">{abbr}</span>
      {show && (
        <span className="omx-tip">
          {full}
        </span>
      )}
    </span>
  );
};

// ─── 10-Step Workflow Pipeline ────────────────────────────────────────────────
const WorkflowPipeline: React.FC<{ currentStatus: string }> = ({ currentStatus }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  // Map status → which step is "current"
  const STATUS_TO_STEP: Record<string, number> = {
    draft: 1, jha_review: 4, prepared: 7, issued: 8,
    tbt_pending: 9, active: 9, suspended: 9,
    surrendered: 10, completed: 10, cancelled: 0,
  };
  const currentStep = STATUS_TO_STEP[currentStatus] ?? 1;

  // Actor classes — a legend below keeps these readable; all three sit on the
  // brand palette and stay mutually distinguishable.
  const typeConfig = {
    auto:  { label: 'Auto',  cls: 'is-auto' },
    human: { label: 'Human', cls: 'is-human' },
    field: { label: 'Field', cls: 'is-field' },
  };

  return (
    <div className="omx-card">
      <div className="omx-card__head">
        <span className="omx-card__title"><GitBranch size={14} />10-Step Automated PTW Pipeline</span>
        <div className="omx-legend">
          <span className="omx-legend__i is-auto">Auto</span>
          <span className="omx-legend__i is-human">Human</span>
          <span className="omx-legend__i is-field">Field</span>
        </div>
      </div>
      <div className="omx-card__body omx-card__body--flush">
        {WORKFLOW_STEPS_CONFIG.map((step) => {
          const tc = typeConfig[step.type];
          const isDone = step.id < currentStep;
          const isActive = step.id === currentStep;
          const isOpen = expanded === step.id;
          const state = isActive ? ' is-current' : isDone ? ' is-done' : '';
          return (
            <div key={step.id}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : step.id)}
                className={`omx-step-row${state}`}
                aria-expanded={isOpen}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="omx-step-row__num">{isDone ? <Check size={11} /> : step.id}</span>
                <span className="omx-step-row__icon">{step.icon}</span>
                <span className="omx-step-row__title">{step.title}</span>
                <span className={`omx-actor ${tc.cls}`}>{tc.label}</span>
                <span className="omx-step-row__target hidden sm:block">{step.target}</span>
                <ChevronDown size={12} className={`omx-step-row__chev${isOpen ? ' is-open' : ''}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }}
                    className="overflow-hidden">
                    <div className="omx-step-row__detail">
                      <p>{step.desc}</p>
                      {step.target && (
                        <p className="omx-step-row__meta"><Clock size={11} />Target: <strong>{step.target}</strong></p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Supplementary Permit Card ────────────────────────────────────────────────
const SuppPermitCard: React.FC<{ type: string }> = ({ type }) => {
  const [open, setOpen] = useState(false);
  const cfg = SUPP_PERMIT_CONFIG[type];
  if (!cfg) return (
    <span className="omx-permit__tag">{type.replace('_', ' ')}</span>
  );
  const Icon = cfg.icon;
  return (
    <div className="omx-supp">
      <button type="button" onClick={() => setOpen(!open)} className="omx-supp__head" aria-expanded={open}>
        <span className="omx-supp__mark"><Icon size={14} /></span>
        <span className="omx-supp__body">
          <span className="omx-supp__label">{cfg.label}</span>
          <span className="omx-supp__ref">{cfg.formRef}</span>
        </span>
        <ChevronDown size={13} className={`omx-step-row__chev${open ? ' is-open' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="overflow-hidden omx-supp__panel">
            <div className="omx-supp__inner space-y-3">
              <div>
                <p className="omx-subhead">Auto-Trigger</p>
                <p className="omx-supp__text">{cfg.trigger}</p>
              </div>
              <div>
                <p className="omx-subhead">Key Requirements</p>
                <ul className="omx-reqs">
                  {cfg.requirements.map((r, i) => (
                    <li key={i}><CheckCircle2 size={11} />{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const WIZARD_STEPS = [
  { id: 1, title: 'Work Order', icon: <ClipboardList size={16} /> },
  { id: 2, title: 'Classification', icon: <Shield size={16} /> },
  { id: 3, title: 'Hazard Flags', icon: <AlertTriangle size={16} /> },
  { id: 4, title: 'Role Assignments', icon: <Users size={16} /> },
  { id: 5, title: 'Review & Submit', icon: <Send size={16} /> },
];

const CreatePTWWizard: React.FC<{ site: string; onClose: () => void; onCreated: (p: PTWPermit) => void }> = ({ site, onClose, onCreated }) => {
  const siteDisplayName = useSiteDisplayName(site);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteUsers, setSiteUsers] = useState<Array<{ id: number; full_name?: string; username: string; role?: string }>>([]);
  const [form, setForm] = useState<Record<string, any>>({
    site_code: site.toUpperCase(), wo_type: 'PREVENTIVE', work_type: 'electrical',
    is_hv_involved: false, validity_hours: 8,
    has_hot_work: false, has_work_at_height: false, has_lifting: false, has_excavation: false, has_confined_space: false,
    // role name mirrors (for display & summary)
    _sc_name: '', _pi_name: '', _pr_name: '', _ia_name: '', _sp_name: '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Load site users for role picker
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res: any = await backendApiService.request('/om/pm-events/assignable-users/', {
          method: 'GET',
          params: { site_code: site }
        });
        const rawUsers = Array.isArray(res?.users) ? res.users : [];
        const users = rawUsers.filter((u: any) => 
          u.role !== 'system_admin' && u.role !== 'super_admin' && u.username !== 'Develop'
        );
        setSiteUsers(users.map((u: any) => ({
          id: u.id,
          full_name: u.full_name || u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
          username: u.username || String(u.id),
          role: u.role || u.role_name || '',
        })));
      } catch {
        try {
          const r2: any = await backendApiService.getUsers(null, null, site);
          const rawUsers2 = Array.isArray(r2) ? r2 : r2?.results || r2?.users || [];
          const users2 = rawUsers2.filter((u: any) => 
            u.role !== 'system_admin' && u.role !== 'super_admin' && u.username !== 'Develop'
          );
          setSiteUsers(users2.map((u: any) => ({
            id: u.id,
            full_name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
            username: u.username || String(u.id),
            role: u.role || '',
          })));
        } catch { }
      }
    };
    loadUsers();
  }, [site]);

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try { const r = await ptwApi.create(form); onCreated(r); onClose(); }
    catch (e: any) { setError(e?.message || 'Failed to create permit.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="omx omx-overlay" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.97, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.97, opacity: 0, y: 12 }}
        transition={{ duration: 0.2 }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New Permit to Work"
        className="omx-modal omx-modal--wide"
        style={{ zIndex: 100001 }}
      >
        <div className="omx-modal__head">
          <div style={{ minWidth: 0 }}>
            <span className="omx-modal__eyebrow">{siteDisplayName} &middot; Work authorisation</span>
            <div className="omx-modal__title">New Permit to Work</div>
            <p className="omx-modal__sub">
              Step {step} of {WIZARD_STEPS.length} &middot; {WIZARD_STEPS[step - 1]?.title}
            </p>
          </div>
          <button onClick={onClose} className="omx-modal__close" aria-label="Close">
            <X size={17} strokeWidth={1.9} />
          </button>
        </div>

        {/* Step rail */}
        <div className="omx-steps">
          {WIZARD_STEPS.map((st) => {
            const done = step > st.id;
            const current = step === st.id;
            return (
              <button
                key={st.id}
                onClick={() => done && setStep(st.id)}
                disabled={!done && !current}
                className={`omx-step${current ? ' is-current' : ''}${done ? ' is-done' : ''}`}
                aria-current={current ? 'step' : undefined}
              >
                <span className="omx-step__mark">
                  {done ? <Check size={11} strokeWidth={2.4} /> : st.id}
                </span>
                <span className="omx-step__label">{st.title}</span>
              </button>
            );
          })}
        </div>

        <div className="omx-modal__body">
          {step === 1 && (
            <div className="omx-formgrid">
              {/* Same Day Fast Track Creation Option (Image 1 - Section 5.3) */}
              <div className="omx-notice omx-notice--warn" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Zap size={15} />
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <p style={{ fontWeight: 600 }}>Same-day fast-track permit creation</p>
                  <p style={{ marginTop: 2 }}>Quick entry for emergency or same-day activity with real-time routing.</p>
                </div>
                <label className="omx-switch">
                  <input type="checkbox" checked={!!form.is_same_day} onChange={e => set('is_same_day', e.target.checked)} />
                  <span className="omx-switch__track" aria-hidden="true" />
                  <span className="sr-only">Enable same-day fast-track</span>
                </label>
              </div>

              <div className="omx-formrow">
                <div>
                  <label className="omx-field-label" htmlFor="ptw-wo-type">Work Order Type <span className="omx-req">*</span></label>
                  <select id="ptw-wo-type" value={form.wo_type} onChange={e => set('wo_type', e.target.value)} className="omx-input">
                    <option value="PREVENTIVE">Preventive (Scheduled PM)</option>
                    <option value="BREAKDOWN">Breakdown (Unplanned Fault)</option>
                    <option value="CORRECTIVE">Corrective (Planned Repair)</option>
                  </select>
                </div>
                <div>
                  <label className="omx-field-label" htmlFor="ptw-work-type">Work Type <span className="omx-req">*</span></label>
                  <select id="ptw-work-type" value={form.work_type} onChange={e => set('work_type', e.target.value)} className="omx-input">
                    {Object.entries(WORK_TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Vendor & Contractor Details (Image 3) */}
              <div className="omx-section">
                <p className="omx-section__title">Vendor &amp; Contractor Details</p>
                <div className="omx-formrow">
                  <div>
                    <label className="omx-field-label" htmlFor="ptw-vendor-co">Vendor / Contractor Company</label>
                    <input id="ptw-vendor-co" type="text" placeholder="Company name" value={form.vendor_company_name || ''} onChange={e => set('vendor_company_name', e.target.value)} className="omx-input" />
                  </div>
                  <div>
                    <label className="omx-field-label" htmlFor="ptw-vendor-person">Vendor Contact Person</label>
                    <input id="ptw-vendor-person" type="text" placeholder="Contact person name" value={form.vendor_person_name || ''} onChange={e => set('vendor_person_name', e.target.value)} className="omx-input" />
                  </div>
                  <div>
                    <label className="omx-field-label" htmlFor="ptw-supervisor">Supervisor / In-Charge</label>
                    <input id="ptw-supervisor" type="text" placeholder="Supervisor name" value={form.supervisor_in_charge || ''} onChange={e => set('supervisor_in_charge', e.target.value)} className="omx-input" />
                  </div>
                  <div>
                    <label className="omx-field-label" htmlFor="ptw-workers">No. of Accompanying Workers</label>
                    <input id="ptw-workers" type="number" min="1" max="100" value={form.no_of_workers || 1} onChange={e => set('no_of_workers', parseInt(e.target.value, 10) || 1)} className="omx-input" />
                  </div>
                </div>
              </div>

              {/* Work Location & Area Hierarchy (Image 1 - Section 5.1 step 4) */}
              <div className="omx-section">
                <p className="omx-section__title">Work Location &amp; Area Hierarchy</p>
                <div className="omx-formrow">
                  <input type="text" placeholder="Plant (e.g. Solar Block 1)" aria-label="Plant" value={form.plant || ''} onChange={e => set('plant', e.target.value)} className="omx-input" />
                  <input type="text" placeholder="Building / structure" aria-label="Building or structure" value={form.building || ''} onChange={e => set('building', e.target.value)} className="omx-input" />
                  <input type="text" placeholder="Department" aria-label="Department" value={form.department || ''} onChange={e => set('department', e.target.value)} className="omx-input" />
                  <input type="text" placeholder="Zone / area" aria-label="Zone or area" value={form.zone_area || ''} onChange={e => set('zone_area', e.target.value)} className="omx-input" />
                  <input type="text" placeholder="Floor / line" aria-label="Floor or line" value={form.floor_line || ''} onChange={e => set('floor_line', e.target.value)} className="omx-input" />
                  <input type="text" placeholder="Equipment KKS tag" aria-label="Equipment KKS tag" value={form.equipment_kks || ''} onChange={e => set('equipment_kks', e.target.value)} className="omx-input" />
                </div>
              </div>

              <div className="omx-section">
                <label className="omx-field-label" htmlFor="ptw-desc">Work Description <span className="omx-req">*</span></label>
                <textarea id="ptw-desc" rows={3} placeholder="Describe the work to be performed…" value={form.work_description || ''} onChange={e => set('work_description', e.target.value)} className="omx-input" style={{ resize: 'none' }} />
                <p className="omx-field-help">Minimum 20 characters &mdash; {(form.work_description || '').length} entered.</p>
              </div>

              <div className="omx-formrow">
                <div>
                  <label className="omx-field-label" htmlFor="ptw-start">Planned Start</label>
                  <input id="ptw-start" type="datetime-local" value={form.planned_start || ''} onChange={e => set('planned_start', e.target.value)} className="omx-input" />
                </div>
                <div>
                  <label className="omx-field-label" htmlFor="ptw-end">Planned End</label>
                  <input id="ptw-end" type="datetime-local" value={form.planned_end || ''} onChange={e => set('planned_end', e.target.value)} className="omx-input" />
                  <p className="omx-field-help">Maximum 12 hours from planned start.</p>
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="omx-formgrid">
              {/* Auto-classification result */}
              <div className="omx-card">
                <div className="omx-card__head">
                  <span className="omx-card__title"><Cpu size={14} />Auto-Classification Engine Result</span>
                  <span className="omx-actor is-auto">Auto</span>
                </div>
                <div className="omx-card__body">
                  <p className="omx-supp__text" style={{ marginBottom: 10 }}>
                    Input: <strong>{form.wo_type}</strong> &middot; <strong>{WORK_TYPE_CONFIG[form.work_type]?.label}</strong>
                    {form.is_hv_involved && <span style={{ color: 'var(--om-err)', fontWeight: 500, marginLeft: 6 }}>&middot; HV involved</span>}
                  </p>
                  {(() => {
                    const match = AUTO_CLASSIFICATION.find(r =>
                      r.wo.indexOf(form.wo_type) > -1 &&
                      (r.hv === !!form.is_hv_involved || !r.hv)
                    );
                    return match ? (
                      <div className="omx-suggest">
                        <div className="omx-suggest__top">
                          <span className="omx-suggest__label">
                            Suggested: {PERMIT_TYPE_CONFIG[match.permitType]?.label || match.permitType}
                          </span>
                          <button type="button" onClick={() => set('permit_type', match.permitType)} className="omx-btn omx-btn--primary omx-btn--sm">
                            Apply
                          </button>
                        </div>
                        <p className="omx-suggest__note">{match.note}</p>
                        {match.hv && (
                          <div className="omx-notice omx-notice--err" style={{ marginTop: 10 }}>
                            <AlertTriangle size={14} />
                            <span>Safety Document mandatory &middot; primary earths required &middot; 3-step test dead.</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="omx-suggest">
                        <p className="omx-suggest__note">Defaulting to Main PTW &mdash; verify work details for precise classification.</p>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* PTW Type Selection Matrix quick-ref */}
              <div>
                <p className="omx-field-label">Select Permit Type</p>
                <p className="omx-field-help" style={{ marginTop: -2, marginBottom: 9 }}>Override if needed &mdash; the Safety Controller must document the reason.</p>
                <div className="space-y-2" role="radiogroup" aria-label="Permit type">
                  {Object.entries(PERMIT_TYPE_CONFIG).map(([k, v]) => (
                    <button key={k} type="button" role="radio" aria-checked={(form.permit_type || 'MAIN') === k}
                      onClick={() => set('permit_type', k)} className="omx-radio">
                      <span className="omx-radio__dot" aria-hidden="true" />
                      <span className="omx-radio__label">{v.label}</span>
                      <span className={`omx-risk ${v.bg}`}>{k}</span>
                    </button>
                  ))}
                </div>
              </div>

              <label className="omx-notice omx-notice--warn" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_hv_involved} onChange={e => set('is_hv_involved', e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ display: 'block', fontWeight: 600, marginBottom: 2 }}>HV equipment involved (&ge; 1 kV)</strong>
                  Requires Safety Document (IC) &middot; SC prepares isolation schedule &middot; primary earths within
                  the isolated zone &middot; 3-step test dead procedure before work.
                </span>
              </label>

              {/* PTW Type quick-reference matrix */}
              <details className="omx-disclose group">
                <summary>
                  <Info size={13} />
                  <span>View full auto-selection matrix</span>
                  <ChevronDown size={13} className="ml-auto group-open:rotate-180 transition-transform" />
                </summary>
                <div className="omx-tablewrap">
                  <table className="omx-table">
                    <thead>
                      <tr>
                        <th>WO Type</th>
                        <th>Equipment</th>
                        <th style={{ textAlign: 'center' }}>HV</th>
                        <th>PTW Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {AUTO_CLASSIFICATION.map((r, i) => (
                        <tr key={i}>
                          <td style={{ color: 'var(--om-blue-2)', fontWeight: 500 }}>{r.wo.join(' / ')}</td>
                          <td>{r.equip}</td>
                          <td style={{ textAlign: 'center' }}>
                            {r.hv ? <Check size={12} style={{ color: 'var(--om-err)', display: 'inline' }} /> : <span style={{ color: 'var(--om-edge)' }}>&mdash;</span>}
                          </td>
                          <td>
                            <span className={`omx-risk ${PERMIT_TYPE_CONFIG[r.permitType]?.bg || ''}`}>{r.permitType}</span>
                            <span style={{ marginLeft: 8, color: 'var(--om-subtle)' }}>{r.note}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
          {step === 3 && (
            <div className="omx-formgrid">
              <p className="omx-supp__text">
                Check all high-risk activities &mdash; the system auto-adds the matching supplementary permits
                with their pre-defined requirements.
              </p>
              {[
                { key: 'has_hot_work', suppKey: 'HOT_WORK', label: 'Hot Work', sub: 'Welding, grinding, cutting, open flame or Ex-zone', icon: <Flame size={16} /> },
                { key: 'has_work_at_height', suppKey: 'WORK_AT_HEIGHT', label: 'Work at Height', sub: '> 1.8 m, unguarded edge, ladder > 3 rungs', icon: <TrendingUp size={16} /> },
                { key: 'has_lifting', suppKey: 'LIFTING', label: 'Lifting Operations', sub: 'Crane / hoist / TEU handling', icon: <Wind size={16} /> },
                { key: 'has_excavation', suppKey: 'EXCAVATION', label: 'Excavation', sub: 'Any dig > 0.5 m depth', icon: <Shovel size={16} /> },
                { key: 'has_confined_space', suppKey: 'CONFINED_SPACE', label: 'Confined Space', sub: 'Cable vault, pit, tunnel, battery room, manhole', icon: <Lock size={16} /> },
              ].map(item => {
                const suppCfg = SUPP_PERMIT_CONFIG[item.suppKey];
                const on = !!form[item.key];
                return (
                  <div key={item.key}>
                    <label className={`omx-risktoggle${on ? ' is-on' : ''}`}>
                      <input type="checkbox" checked={on} onChange={e => set(item.key, e.target.checked)} />
                      <span className="omx-risktoggle__mark">{item.icon}</span>
                      <span className="omx-risktoggle__body">
                        <span className="omx-risktoggle__label">{item.label}</span>
                        <span className="omx-risktoggle__sub">{item.sub}</span>
                        {suppCfg && <span className="omx-risktoggle__ref">Form: {suppCfg.formRef}</span>}
                      </span>
                      {on && <span className="omx-pill">+ Supp. permit</span>}
                    </label>
                    {on && suppCfg && (
                      <div className="omx-risktoggle__detail">
                        <p className="omx-subhead">Key requirements auto-checked</p>
                        <ul className="omx-reqs">
                          {suppCfg.requirements.map((req, i) => (
                            <li key={i}><Check size={11} />{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* WBGT / Heat Alert */}
              <div className="omx-notice omx-notice--warn">
                <Thermometer size={15} />
                <span>
                  <strong style={{ display: 'block', fontWeight: 600, marginBottom: 2 }}>WBGT heat alert (auto)</strong>
                  Above 28&deg;C WBGT the system adds a mandatory 10-minute break per 50 minutes and requires a
                  hydration station within 30 m. Monitored via the site weather integration.
                </span>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="omx-formgrid">
              <div className="omx-notice">
                <Info size={15} />
                <span>
                  All assigned users receive automatic in-app and email notifications at their step. Only users
                  registered on site <strong>{siteDisplayName}</strong> are shown.
                </span>
              </div>
              <div className="omx-formgrid">
                {[
                  { idKey: 'safety_controller_id', nameKey: '_sc_name', label: 'Safety Controller (SC)', desc: 'Reviews JHA, locks isolation schedule, signs Prepared', required: true },
                  { idKey: 'permit_issuer_id',     nameKey: '_pi_name', label: 'Permit Issuer (PI)',    desc: 'Issues PTW digitally, activates key safe', required: true },
                  { idKey: 'permit_receiver_id',   nameKey: '_pr_name', label: 'Permit Receiver (PR)', desc: 'Conducts TBT, supervises work, surrenders permit', required: true },
                  { idKey: 'isolation_authority_id', nameKey: '_ia_name', label: 'Isolation Authority (IA)', desc: 'Performs and confirms each isolation point', required: false },
                  { idKey: 'skilled_person_id',    nameKey: '_sp_name', label: 'Skilled Person (SP)',   desc: 'Reviews & approves auto-generated JHA (< 2 min)', required: false },
                ].map(role => (
                  <UserPicker
                    key={role.idKey}
                    label={role.label}
                    desc={role.desc}
                    required={role.required}
                    value={form[role.idKey] || ''}
                    selectedName={form[role.nameKey] || ''}
                    users={siteUsers}
                    onChange={(id, name) => setForm(f => ({ ...f, [role.idKey]: id, [role.nameKey]: name }))}
                  />
                ))}
              </div>
              {siteUsers.length === 0 && (
                <div className="omx-notice omx-notice--warn">
                  <AlertCircle size={15} />
                  <span>No users loaded for this site. You can still create the permit and assign roles later.</span>
                </div>
              )}
            </div>
          )}
          {step === 5 && (
            <div className="omx-formgrid">
              <div className="omx-card">
                <div className="omx-card__head">
                  <span className="omx-card__title"><Cpu size={14} />Auto-generated on creation</span>
                  <span className="omx-actor is-auto">Steps 1&ndash;6</span>
                </div>
                <div className="omx-card__body">
                  <ul className="omx-reqs">
                    <li><Check size={11} />Permit number: PTW-{site.toUpperCase()}-{new Date().getFullYear()}-XXXX</li>
                    <li><Check size={11} />JHA auto-populated from best-match template (SP review &lt; 2 min)</li>
                    <li><Check size={11} />PPE list auto-selected from JHA hazard codes (4 mandatory items always included)</li>
                    <li><Check size={11} />Tools checklist auto-populated ({TOOLS_MASTER.length} items in master list)</li>
                    {form.is_hv_involved && (
                      <li className="is-alert"><AlertTriangle size={11} />Isolation schedule auto-generated from the SLD database via KKS tag</li>
                    )}
                    {(form.has_hot_work || form.has_work_at_height || form.has_lifting || form.has_excavation || form.has_confined_space) && (
                      <li><Check size={11} />
                        {[form.has_hot_work && 'Hot Work', form.has_work_at_height && 'WAH', form.has_lifting && 'Lifting', form.has_excavation && 'Excavation', form.has_confined_space && 'Confined Space'].filter(Boolean).join(', ')} supplementary permit(s) created
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="omx-card">
                <div className="omx-card__head">
                  <span className="omx-card__title"><FileText size={14} />Summary</span>
                </div>
                <div className="omx-card__body">
                  <div className="omx-dl">
                    {([
                      ['WO Type', form.wo_type],
                      ['Work Type', WORK_TYPE_CONFIG[form.work_type]?.label || form.work_type],
                      ['Permit Type', PERMIT_TYPE_CONFIG[form.permit_type || 'MAIN']?.label],
                      ['HV Involved', form.is_hv_involved ? 'Yes — Safety Document (IC) required' : 'No'],
                      ['Equipment KKS', form.equipment_kks || '\u2014'],
                      ['Work Description', (form.work_description || '\u2014').slice(0, 80) + ((form.work_description || '').length > 80 ? '\u2026' : '')],
                      ['Safety Controller', form._sc_name || (form.safety_controller_id ? `User #${form.safety_controller_id}` : 'Assign after creation')],
                      ['Permit Issuer', form._pi_name || (form.permit_issuer_id ? `User #${form.permit_issuer_id}` : 'Assign after creation')],
                      ['Permit Receiver', form._pr_name || (form.permit_receiver_id ? `User #${form.permit_receiver_id}` : 'Assign after creation')],
                      ['Validity', `${form.validity_hours || 8} hours (max 12 h, cannot cross midnight)`],
                    ] as [string, string][]).map(([k, v]) => (
                      <div key={k} className="omx-dl__row">
                        <span className="omx-dl__k">{k}</span>
                        <span className="omx-dl__v" style={String(v).indexOf('Yes —') === 0 ? { color: 'var(--om-err)' } : undefined}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Next steps reminder */}
              <div className="omx-notice" style={{ flexDirection: 'column', gap: 8 }}>
                <p className="omx-section__title" style={{ marginBottom: 0 }}>After creation &mdash; workflow continues automatically</p>
                <ol className="omx-ol">
                  <li><b>7.</b> <strong>SC</strong> reviews JHA + isolation schedule, signs &ldquo;Prepared&rdquo; (&lt; 5 min)</li>
                  <li><b>8.</b> <strong>PI</strong> issues PTW to PR and activates the key safe (&lt; 3 min)</li>
                  <li><b>9.</b> <strong>PR</strong> conducts TBT, <strong>IA</strong> performs isolation per schedule</li>
                  <li><b>10.</b> Closure: PR surrenders &rarr; SC de-isolates &rarr; CMMS WO auto-closed</li>
                </ol>
              </div>

              {error && <div className="omx-notice omx-notice--err"><AlertCircle size={15} strokeWidth={1.9} />{error}</div>}
            </div>
          )}
        </div>
        <div className="omx-modal__foot omx-modal__foot--split">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="omx-btn">
            <ArrowLeft size={14} strokeWidth={1.9} />{step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < WIZARD_STEPS.length ? (
            <button type="button" onClick={() => setStep(step + 1)} disabled={step === 1 && (form.work_description || '').length < 20}
              className="omx-btn omx-btn--primary">
              Next <ChevronRight size={14} strokeWidth={1.9} />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={loading} className="omx-btn omx-btn--primary">
              {loading ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={1.9} />}
              {loading ? 'Creating…' : 'Create Permit'}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const PermitDetail: React.FC<{
  permit: PTWPermit;
  currentUser: CurrentUser | null;
  onClose: () => void;
  onAction: (a: string, id: number, extra?: any) => Promise<void>;
  actionLoading: string;
}> = ({ permit, currentUser, onClose, onAction, actionLoading }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'jha' | 'ppe' | 'tools' | 'isolation' | 'tbt' | 'timeline' | 'flow'>('overview');
  const [sigText, setSigText] = useState('');
  const [tbtForm, setTBTForm] = useState({ location: '', workers: [{ worker_name: '', worker_id_no: '', signature_text: '' }] });
  const [reasonModal, setReasonModal] = useState<{ action: string; title: string; placeholder: string; danger?: boolean } | null>(null);
  const wt = WORK_TYPE_CONFIG[permit.work_type] || WORK_TYPE_CONFIG.general;
  const WorkTypeIcon = wt.icon;
  const addWorker = () => setTBTForm(f => ({ ...f, workers: [...f.workers, { worker_name: '', worker_id_no: '', signature_text: '' }] }));
  const updateWorker = (i: number, field: string, val: string) => setTBTForm(f => ({ ...f, workers: f.workers.map((w, idx) => idx === i ? { ...w, [field]: val } : w) }));
  const fallback = (v: any, text = 'Awaiting update') => (v === null || v === undefined || v === '' ? text : v);
  const fmtDate = (v?: string) => (v ? new Date(v).toLocaleString() : 'Not recorded');
  const sidebarBoldStyle = { fontWeight: 900 as const, WebkitTextStroke: '0.4px currentColor' };

  const pickFirstNonEmpty = (...values: Array<any>) => {
    for (let index = 0; index < values.length; index += 1) {
      const value = values[index];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return '';
  };

  const findSignatureByRole = (role: string) => {
    const signatures = permit.signatures || [];
    for (let index = 0; index < signatures.length; index += 1) {
      const signature = signatures[index];
      if ((signature?.ptw_role || '') === role) {
        return signature;
      }
    }
    return null;
  };

  const findAuditByAction = (action: string) => {
    const logs = permit.audit_logs || [];
    for (let index = 0; index < logs.length; index += 1) {
      const log = logs[index];
      if ((log?.action || '') === action) {
        return log;
      }
    }
    return null;
  };

  const displayNames = {
    sc: fallback(
      pickFirstNonEmpty(
        permit.sc_name,
        findSignatureByRole('SC')?.signed_by_name,
        permit.safety_controller_id ? `User #${permit.safety_controller_id}` : ''
      )
    ),
    pi: fallback(
      pickFirstNonEmpty(
        permit.pi_name,
        findSignatureByRole('PI')?.signed_by_name,
        permit.permit_issuer_id ? `User #${permit.permit_issuer_id}` : ''
      )
    ),
    pr: fallback(
      pickFirstNonEmpty(
        permit.pr_name,
        findSignatureByRole('PR')?.signed_by_name,
        (permit.tbt_records || [])[0]?.conducted_by_name,
        permit.permit_receiver_id ? `User #${permit.permit_receiver_id}` : ''
      )
    ),
    ia: fallback(
      pickFirstNonEmpty(
        permit.ia_name,
        (permit.isolation_items || [])[0]?.ia_signed_by_name,
        permit.isolation_authority_id ? `User #${permit.isolation_authority_id}` : ''
      )
    ),
    sp: fallback(
      pickFirstNonEmpty(
        permit.sp_name,
        findSignatureByRole('SP')?.signed_by_name,
        permit.skilled_person_id ? `User #${permit.skilled_person_id}` : ''
      )
    ),
  };

  const checkpointDates = {
    created: pickFirstNonEmpty(permit.created_at, findAuditByAction('CREATED')?.performed_at),
    issued: pickFirstNonEmpty(permit.issued_at, findSignatureByRole('PI')?.signed_at, findAuditByAction('PERMIT_ISSUED')?.performed_at),
    tbtConducted: pickFirstNonEmpty((permit as any).tbt_conducted_at, (permit.tbt_records || [])[0]?.conducted_at, findAuditByAction('TBT_CONDUCTED')?.performed_at),
    workStarted: pickFirstNonEmpty(permit.work_started_at, (permit.tbt_records || [])[0]?.conducted_at, findAuditByAction('TBT_CONDUCTED')?.performed_at),
  };

  // ── Role detection ──────────────────────────────────────────────────────────
  const myRole = getMyRoleOnPermit(permit, currentUser);
  const myRoleCfg = ROLE_ON_PERMIT_LABELS[myRole];
  const allowedActions: string[] = (ROLE_STATUS_ACTIONS[myRole]?.[permit.status] || []);
  const can = (action: string) => allowedActions.indexOf(action) > -1;
  const nextAction = STATUS_NEXT_ACTION[permit.status];
  // Is it my turn to act?
  const isMyTurn = allowedActions.length > 0 && permit.status !== 'completed' && permit.status !== 'cancelled';

  const TABS = [
    { id: 'overview',  label: 'Overview',   count: null,                                icon: <FileText size={14} /> },
    { id: 'flow',      label: 'Pipeline',   count: null,                                icon: <GitBranch size={14} /> },
    { id: 'jha',       label: 'JHA',        count: (permit.jha_items || []).length,      icon: <AlertTriangle size={14} /> },
    { id: 'ppe',       label: 'PPE',        count: (permit.ppe_items || []).length,      icon: <HardHat size={14} /> },
    { id: 'tools',     label: 'Tools',      count: null,                                icon: <Wrench size={14} /> },
    { id: 'isolation', label: 'Isolation',  count: (permit.isolation_items || []).length, icon: <Lock size={14} /> },
    { id: 'tbt',       label: 'TBT',        count: null,                                icon: <Users size={14} /> },
    { id: 'timeline',  label: 'Timeline',   count: null,                                icon: <Activity size={14} /> },
  ] as const;

  return (
    <>
    <div className="omx omx-overlay omx-overlay--right" onClick={onClose}>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.2 }}
        className="omx-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Permit ${permit.permit_no}`}
        style={{ zIndex: 100001 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="omx-drawer__head">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 34, height: 34, borderRadius: 3,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  color: '#FFFFFF',
                }}
              >
                <WorkTypeIcon size={16} />
              </span>
              <div className="min-w-0">
                <p className="omx-modal__eyebrow">
                  {PERMIT_TYPE_CONFIG[permit.permit_type]?.label || 'Permit'} • {wt.label}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="omx-modal__title truncate">{permit.permit_no}</h2>
                  <StatusBadge status={permit.status} />
                  {myRole !== 'VIEWER' && (
                    <span className={`omx-role ${myRoleCfg.badgeCls}`}>
                      <User size={10} />You: {myRoleCfg.label}
                    </span>
                  )}
                </div>
                <div className="omx-drawer__meta">
                  <span className="omx-drawer__chip"><b>WO</b>{fallback(permit.wo_number, 'Not linked')}</span>
                  <span className="omx-drawer__chip"><b>JHA</b>{fallback(permit.jha_ref_no)}</span>
                  <span className="omx-drawer__chip"><b>Key Safe</b>{fallback(permit.key_safe_number, 'Pending')}</span>
                </div>
              </div>
            </div>
            <button type="button" onClick={onClose} className="omx-modal__close" aria-label="Close permit details">
              <X size={16} />
            </button>
          </div>
          {/* Action-needed banner — only shown if it's this user's turn */}
          {isMyTurn && (
            <div className="omx-drawer__banner">
              <Bell size={13} />
              <p>
                <strong>Action required from you ({myRole})</strong>
                {nextAction ? ` — ${nextAction.message}` : ''}
              </p>
            </div>
          )}
          {/* Query Notes Banner if SP raised a query */}
          {permit.query_notes && (
            <div className="omx-drawer__banner omx-drawer__banner--warn">
              <AlertCircle size={14} />
              <div>
                <strong>Query raised by Skilled Person (SP)</strong>
                <p style={{ marginTop: 3 }}>{permit.query_notes}</p>
                <p style={{ marginTop: 5, fontSize: 11, opacity: 0.85 }}>Review or update the permit details / JHA items, then click 'Submit for JHA Review' to re-submit.</p>
              </div>
            </div>
          )}
        </div>

        <div className="omx-tabs" role="tablist" aria-label="Permit sections">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`omx-tab${activeTab === tab.id ? ' is-active' : ''}`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== null && <span className="omx-tab__count">{tab.count}</span>}
            </button>
          ))}
        </div>

        <div className={`omx-drawer__body ${activeTab === 'overview' ? 'p-3' : 'p-5'}`} style={activeTab === 'overview' ? { overflowY: 'hidden' } : undefined}>
          {activeTab === 'overview' && (
            <div className="space-y-3 h-full">
              <div className="omx-tiles">
                <div className="omx-tile">
                  <span className="omx-tile__k">WO Type</span>
                  <span className="omx-tile__v">{fallback(permit.wo_type)}</span>
                </div>
                <div className="omx-tile">
                  <span className="omx-tile__k">Equipment KKS</span>
                  <span className="omx-tile__v">{fallback(permit.equipment_kks)}</span>
                </div>
                <div className="omx-tile">
                  <span className="omx-tile__k">HV Involved</span>
                  <span className={`omx-tile__v ${permit.is_hv_involved ? 'is-alert' : 'is-clear'}`}>{permit.is_hv_involved ? 'Yes' : 'No'}</span>
                </div>
                <div className="omx-tile">
                  <span className="omx-tile__k">Permit Receiver</span>
                  <span className="omx-tile__v">{displayNames.pr}</span>
                </div>
              </div>

              <div className="omx-card">
                <div className="omx-card__body">
                  <p className="omx-subhead">Work Description</p>
                  <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--om-muted)' }}>{fallback(permit.work_description, 'Description not provided')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="omx-card">
                  <div className="omx-card__body">
                    <p className="omx-subhead">Responsibilities &amp; Window</p>
                    <div className="omx-dl">
                      {([
                        ['Safety Controller', displayNames.sc],
                        ['Permit Issuer', displayNames.pi],
                        ['Permit Receiver', displayNames.pr],
                        ['Planned Start', fmtDate(permit.planned_start)],
                        ['Planned End', fmtDate(permit.planned_end)],
                      ] as [string, string][]).map(([label, value]) => (
                        <div key={label} className="omx-dl__row">
                          <span className="omx-dl__k">{label}</span>
                          <span className="omx-dl__v">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="omx-card">
                  <div className="omx-card__body">
                    <p className="omx-subhead">Workflow Checkpoints</p>
                    <div className="omx-dl">
                      {([
                        ['Created', fmtDate(checkpointDates.created)],
                        ['Issued', fmtDate(checkpointDates.issued)],
                        ['TBT Conducted', fmtDate(checkpointDates.tbtConducted)],
                        ['Work Started', fmtDate(checkpointDates.workStarted)],
                      ] as [string, string][]).map(([label, value]) => (
                        <div key={label} className="omx-dl__row">
                          <span className="omx-dl__k">{label}</span>
                          <span className="omx-dl__v">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {permit.sup_permit_types && permit.sup_permit_types.length > 0 && (
                <div className="omx-card">
                  <div className="omx-card__head">
                    <span className="omx-card__title"><FileText size={14} />Supplementary Permits</span>
                  </div>
                  <div className="omx-card__body space-y-2">
                    {permit.sup_permit_types.map(t => <SuppPermitCard key={t} type={t} />)}
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'jha' && (
            <div className="space-y-3">
              <div className="omx-hint">
                <ClipboardList size={12} />
                <span>Auto-generated from template &mdash; the Skilled Person reviews every entry before approval.</span>
              </div>
              {(permit.jha_items || []).length === 0 && (
                <div className="omx-state">
                  <span className="omx-state__icon"><ClipboardList size={22} /></span>
                  <p className="omx-state__title">No JHA items yet</p>
                  <p className="omx-state__text">Hazards and control measures appear here once the job hazard analysis is generated.</p>
                </div>
              )}
              {(permit.jha_items || []).map((item: any) => (
                <div key={item.id} className="omx-jha">
                  <div className="omx-jha__top">
                    <span className="omx-jha__code">{item.hazard_code}</span>
                    <span className="omx-jha__risk">
                      <RiskBadge risk={item.risk_before} />
                      <ChevronRight size={12} />
                      <RiskBadge risk={item.residual_risk} />
                    </span>
                  </div>
                  <p className="omx-jha__step">{item.task_step}</p>
                  <p className="omx-jha__hazard">{item.hazard}</p>
                  <div className="omx-jha__control">
                    <p>Control Measure</p>
                    <p>{item.control_measure}</p>
                  </div>
                  <div className="omx-jha__foot">
                    <span>Responsible: <strong>{item.responsible || '\u2014'}</strong></span>
                    {item.sp_notes && <span>SP note: <strong>{item.sp_notes}</strong></span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'ppe' && (
            <div className="space-y-3">
              <div className="omx-hint">
                <HardHat size={12} />
                <span>Auto-selected from hazard codes &mdash; items marked <strong>mandatory</strong> cannot be waived.</span>
              </div>
              {(permit.ppe_items || []).length === 0 && (
                <div>
                  <div className="omx-hint" style={{ marginBottom: 8 }}>
                    <Info size={12} />
                    <span>Showing master reference list ({PPE_MASTER.length} items).</span>
                  </div>
                  {PPE_MASTER.map((ppe, i) => (
                    <div key={i} className={`omx-item${ppe.mandatory ? ' is-required' : ''}`}>
                      <span className={`omx-item__mark${ppe.mandatory ? ' is-on' : ''}`}>
                        {ppe.mandatory && <Check size={11} />}
                      </span>
                      <div className="omx-item__body">
                        <p className="omx-item__name">
                          {ppe.item}{ppe.mandatory && <span className="omx-tag-req">Mandatory</span>}
                        </p>
                        <p className="omx-item__meta">{ppe.standard}</p>
                      </div>
                      <span className="omx-item__aside hidden sm:block">{ppe.autoIf}</span>
                    </div>
                  ))}
                </div>
              )}
              {(permit.ppe_items || []).length > 0 && (
                <div>
                  {(permit.ppe_items || []).map((item: any) => (
                    <div key={item.id} className={`omx-item${item.is_mandatory ? ' is-required' : ''}${item.is_selected ? '' : ' is-off'}`}>
                      <span className={`omx-item__mark${item.is_selected ? ' is-on' : ''}`}>
                        {item.is_selected && <Check size={11} />}
                      </span>
                      <div className="omx-item__body">
                        <p className="omx-item__name">
                          {item.item_name}{item.is_mandatory && <span className="omx-tag-req">Mandatory</span>}
                        </p>
                        <p className="omx-item__meta">{item.standard_rating}</p>
                      </div>
                      <span className="omx-item__aside">{item.hazard_flag}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'tools' && (
            <div className="space-y-3">
              <div className="omx-hint">
                <Wrench size={12} />
                <span>Auto-populated from JHA hazard codes &mdash; any tool with <strong>expired calibration</strong> must be replaced before work starts.</span>
              </div>
              {(permit.tool_items || []).length === 0 && (
                <div>
                  <div className="omx-hint" style={{ marginBottom: 8 }}>
                    <Info size={12} />
                    <span>Showing master reference list ({TOOLS_MASTER.length} items).</span>
                  </div>
                  {TOOLS_MASTER.map((tool, i) => (
                    <div key={i} className="omx-item">
                      <span className="omx-item__mark"><Wrench size={10} /></span>
                      <div className="omx-item__body">
                        <p className="omx-item__name">{tool.tool}</p>
                        <p className="omx-item__meta">{tool.spec}</p>
                        <p className="omx-item__meta">{tool.autoIf}</p>
                      </div>
                      {tool.calibRequired && <span className="omx-pill omx-pill--warn">Cal. required</span>}
                    </div>
                  ))}
                </div>
              )}
              {(permit.tool_items || []).length > 0 && (
                <div>
                  {(permit.tool_items || []).map((item: any) => (
                    <div key={item.id} className={`omx-item${item.is_selected ? '' : ' is-off'}`}>
                      <span className={`omx-item__mark${item.is_selected ? ' is-on' : ''}`}>
                        {item.is_selected ? <Check size={11} /> : <Wrench size={10} />}
                      </span>
                      <div className="omx-item__body">
                        <p className="omx-item__name">{item.tool_name}</p>
                        <p className="omx-item__meta">{item.specification}</p>
                      </div>
                      {item.calibration_required && (
                        <span className={`omx-pill ${item.is_calibrated ? 'omx-pill--ok' : 'omx-pill--err'}`}>
                          {item.is_calibrated ? 'Cal. OK' : 'Cal. due'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'flow' && (
            <div className="space-y-3">
              <WorkflowPipeline currentStatus={permit.status} />

              {permit.sup_permit_types && permit.sup_permit_types.length > 0 && (
                <div className="omx-card">
                  <div className="omx-card__head">
                    <span className="omx-card__title"><FileText size={14} />Supplementary Permits Attached</span>
                  </div>
                  <div className="omx-card__body space-y-2">
                    {permit.sup_permit_types.map(t => <SuppPermitCard key={t} type={t} />)}
                  </div>
                </div>
              )}

              <div className="omx-card">
                <div className="omx-card__head">
                  <span className="omx-card__title"><Users size={14} />Role Assignments &amp; Responsibilities</span>
                </div>
                <div className="omx-card__body">
                  {[
                    { abbr: 'SC', name: displayNames.sc, action: 'Reviews JHA, locks isolation schedule, signs Prepared (< 5 min)' },
                    { abbr: 'PI', name: displayNames.pi, action: 'Verifies PR authorization, issues PTW digitally, activates e-key safe (< 3 min)' },
                    { abbr: 'PR', name: displayNames.pr, action: 'Conducts TBT, confirms workers signed, supervises work, surrenders permit' },
                  ].map(role => (
                    <div key={role.abbr} className="omx-rolerow">
                      <span className="omx-rolerow__abbr">{role.abbr}</span>
                      <div className="flex-1 min-w-0">
                        <p className="omx-rolerow__name">{fallback(role.name, `No ${role.abbr} assigned`)}</p>
                        <p className="omx-rolerow__task">{role.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <details className="omx-disclose group">
                <summary>
                  <BookOpen size={13} />
                  <span>Abbreviations glossary</span>
                  <ChevronDown size={13} className="ml-auto group-open:rotate-180 transition-transform" />
                </summary>
                <div className="omx-disclose__body" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  <div className="omx-glossary">
                    {Object.entries(ABBREVIATIONS).map(([abbr, full]) => (
                      <div key={abbr}><b>{abbr}</b><span>{full}</span></div>
                    ))}
                  </div>
                </div>
              </details>
            </div>
          )}
          {activeTab === 'isolation' && (
            <div className="space-y-3">
              <div className="omx-hint">
                <Lock size={12} />
                <span>The Isolation Authority confirms each point &mdash; de-isolation runs in <strong>strict reverse order</strong> when the permit closes.</span>
              </div>
              {(permit.isolation_items || []).length === 0 && (
                <div className="omx-state">
                  <span className="omx-state__icon"><Lock size={22} /></span>
                  <p className="omx-state__title">No isolation points</p>
                  <p className="omx-state__text">
                    {permit.is_hv_involved
                      ? 'No isolation points have been recorded against this permit yet.'
                      : 'This permit covers LV / mechanical work, so no electrical isolation schedule is required.'}
                  </p>
                </div>
              )}
              {(permit.isolation_items || []).length > 0 && (
                <div>
                  {(permit.isolation_items || []).map((item: any) => (
                    <div key={item.id} className={`omx-iso${item.is_isolated ? ' is-done' : ''}`}>
                      <div className="omx-iso__top">
                        <span className="omx-iso__seq">#{item.sequence_no} &middot; {item.kks_tag}</span>
                        {item.is_isolated
                          ? <span className="omx-pill omx-pill--ok"><CheckCircle2 size={11} />Isolated</span>
                          : <span className="omx-pill omx-pill--idle">Pending</span>}
                      </div>
                      <p className="omx-iso__equip">{item.equipment_desc}</p>
                      <div className="omx-iso__meta">
                        <span>Action: <strong>{item.action}</strong></span>
                        <span>Lock <strong>{item.lock_no}</strong> / Tag <strong>{item.tag_no}</strong></span>
                      </div>
                      {item.ia_signed_at && (
                        <p className="omx-iso__sign">Confirmed by IA &middot; {new Date(item.ia_signed_at).toLocaleString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {permit.is_hv_involved && (permit.isolation_items || []).length > 0 && (
                <div className="omx-notice omx-notice--warn">
                  <AlertTriangle size={15} />
                  <span>The IA must confirm <strong>every</strong> point isolated before work starts. SC and IA co-sign isolation complete.</span>
                </div>
              )}
            </div>
          )}
          {activeTab === 'tbt' && (
            <div className="space-y-3">
              {(permit.tbt_records || []).map((tbt: any) => (
                <div key={tbt.id} className="omx-card">
                  <div className="omx-card__head">
                    <span className="omx-card__title"><CheckCircle2 size={14} />Toolbox Talk Conducted</span>
                    <span className="omx-pill omx-pill--ok">Complete</span>
                  </div>
                  <div className="omx-card__body">
                    <p className="omx-tl__who" style={{ marginTop: 0, marginBottom: 10 }}>{new Date(tbt.conducted_at).toLocaleString()}</p>
                    <p className="omx-subhead">Workers Signed ({(tbt.workers || []).length})</p>
                    <div className="omx-dl">
                      {(tbt.workers || []).map((w: any, i: number) => (
                        <div key={i} className="omx-dl__row">
                          <span className="omx-dl__k" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                            <Check size={12} style={{ color: 'var(--om-ok)' }} />{w.worker_name}
                          </span>
                          <span className="omx-dl__v">{w.worker_id_no || '\u2014'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {permit.status === 'tbt_pending' && (
                <div className="omx-card">
                  <div className="omx-card__head">
                    <span className="omx-card__title"><Users size={14} />Conduct Toolbox Talk</span>
                  </div>
                  <div className="omx-card__body omx-formgrid">
                    <div>
                      <label className="omx-field-label" htmlFor="tbt-location">Location</label>
                      <input id="tbt-location" type="text" placeholder="Where the talk is being held"
                        value={tbtForm.location} onChange={e => setTBTForm(f => ({ ...f, location: e.target.value }))}
                        className="omx-input" />
                    </div>
                    <div>
                      <p className="omx-field-label">Workers &mdash; all must sign</p>
                      <div className="space-y-2">
                        {tbtForm.workers.map((w, i) => (
                          <div key={i} className="omx-formrow">
                            <input type="text" placeholder="Full name *" value={w.worker_name} onChange={e => updateWorker(i, 'worker_name', e.target.value)} className="omx-input" aria-label={`Worker ${i + 1} name`} />
                            <input type="text" placeholder="ID no." value={w.worker_id_no} onChange={e => updateWorker(i, 'worker_id_no', e.target.value)} className="omx-input" aria-label={`Worker ${i + 1} ID`} />
                            <input type="text" placeholder="e-Signature" value={w.signature_text} onChange={e => updateWorker(i, 'signature_text', e.target.value)} className="omx-input" aria-label={`Worker ${i + 1} signature`} />
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addWorker} className="omx-btn" style={{ marginTop: 9 }}>
                        <Plus size={13} />Add worker
                      </button>
                    </div>
                    <button type="button" onClick={() => onAction('conductTBT', permit.id, { ...tbtForm, signature_text: sigText })}
                      disabled={actionLoading === `conductTBT_${permit.id}`} className="omx-btn omx-btn--primary" style={{ width: '100%' }}>
                      {actionLoading === `conductTBT_${permit.id}` ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                      Conduct TBT &amp; start work
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'timeline' && (
            <div className="omx-tl">
              {(permit.audit_logs || []).length === 0 && (
                <div className="omx-state">
                  <span className="omx-state__icon"><Activity size={22} /></span>
                  <p className="omx-state__title">No audit history yet</p>
                  <p className="omx-state__text">Every signature, status change and note on this permit will be recorded here.</p>
                </div>
              )}
              {(permit.audit_logs || []).map((log: any, i: number) => (
                <div key={log.id || i} className="omx-tl__row">
                  <div className="omx-tl__gutter">
                    <span className="omx-tl__dot" />
                    {i < (permit.audit_logs || []).length - 1 && <span className="omx-tl__line" />}
                  </div>
                  <div className="omx-tl__body">
                    <p className="omx-tl__action">{log.action?.replace(/_/g, ' ').toLowerCase()}</p>
                    <p className="omx-tl__who">{log.performed_by_name || 'System'} &middot; {new Date(log.performed_at).toLocaleString()}</p>
                    {(log.old_value || log.new_value) && (
                      <p className="omx-tl__delta">{log.old_value && <><s>{log.old_value}</s> &rarr; </>}{log.new_value}</p>
                    )}
                    {log.notes && <p className="omx-tl__note">{log.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Role-gated action footer */}
        <div className="omx-actionbar">
          {myRole === 'VIEWER' && (permit.status === 'completed' || permit.status === 'cancelled') && (
            <p className="omx-actionbar__note">
              <CheckCircle2 size={14} style={{ color: permit.status === 'completed' ? 'var(--om-ok)' : 'var(--om-err)' }} />
              {permit.status === 'completed' ? 'Permit closed and archived.' : 'Permit cancelled.'}
            </p>
          )}
          {myRole === 'VIEWER' && permit.status !== 'completed' && permit.status !== 'cancelled' && (
            <div className="omx-notice">
              <Eye size={15} />
              <span>View-only access. You are not assigned a role on this permit.</span>
            </div>
          )}

          {/* Signature field — only for actions that need it */}
          {(can('spApproveJHA') || can('scPrepare') || can('piIssue') || can('surrender') || can('close')) && (
            <div className="omx-actionbar__sig">
              <label className="omx-field-label" htmlFor={`ptw-sig-${permit.id}`}>
                Electronic signature &mdash; type your full name
              </label>
              <input
                id={`ptw-sig-${permit.id}`}
                type="text"
                placeholder="e.g. John Smith — confirming identity for this action"
                value={sigText}
                onChange={e => setSigText(e.target.value)}
                className="omx-input"
              />
            </div>
          )}

          {/* Role-gated buttons */}
          <div className="omx-actionbar__row">
            {can('submitForReview') && (
              <button type="button" onClick={() => onAction('submitForReview', permit.id)} disabled={!!actionLoading} className="omx-btn omx-btn--primary">
                <Send size={13} />Submit for JHA review
              </button>
            )}
            {can('spApproveJHA') && (
              <>
                <button type="button" onClick={() => onAction('spApproveJHA', permit.id, { signature_text: sigText, decision: 'approve' })} disabled={!sigText || !!actionLoading} className="omx-btn omx-btn--primary">
                  <ThumbsUp size={13} />SP: Approve JHA
                </button>
                <button type="button" onClick={() => setReasonModal({ action: 'spApproveJHA_query', title: 'Raise Query on JHA', placeholder: 'Enter your query for creator/team...' })} disabled={!!actionLoading} className="omx-btn omx-btn--warn">
                  <AlertCircle size={13} />Raise query
                </button>
                <button type="button" onClick={() => setReasonModal({ action: 'spApproveJHA_reject', title: 'Reject JHA', placeholder: 'Reason for JHA rejection...', danger: true })} disabled={!!actionLoading} className="omx-btn omx-btn--danger">
                  <XCircle size={13} />Reject JHA
                </button>
              </>
            )}
            {can('scPrepare') && (
              <button type="button" onClick={() => onAction('scPrepare', permit.id, sigText)} disabled={!sigText || !!actionLoading} className="omx-btn omx-btn--primary">
                <Shield size={13} />SC: Prepare &amp; lock
              </button>
            )}
            {can('piIssue') && (
              <button type="button" onClick={() => onAction('piIssue', permit.id, sigText)} disabled={!sigText || !!actionLoading} className="omx-btn omx-btn--primary">
                <Key size={13} />PI: Issue permit
              </button>
            )}
            {can('suspend') && (
              <button type="button" onClick={() => setReasonModal({ action: 'suspend', title: 'Suspend Work', placeholder: 'Reason for suspension (safety hazard, weather, resource unavailable…)' })} className="omx-btn omx-btn--warn">
                <ToggleLeft size={13} />Suspend
              </button>
            )}
            {can('resume') && (
              <button type="button" onClick={() => onAction('resume', permit.id)} disabled={!!actionLoading} className="omx-btn omx-btn--primary">
                <ToggleRight size={13} />Resume work
              </button>
            )}
            {can('surrender') && (
              <button type="button" onClick={() => onAction('surrender', permit.id, sigText)} disabled={!sigText || !!actionLoading} className="omx-btn omx-btn--primary">
                <LogOut size={13} />PR: Surrender permit
              </button>
            )}
            {can('close') && (
              <button type="button" onClick={() => onAction('close', permit.id, sigText)} disabled={!sigText || !!actionLoading} className="omx-btn omx-btn--primary">
                <CheckCircle2 size={13} />SC: Close permit
              </button>
            )}
            {can('cancel') && (
              <button type="button" onClick={() => setReasonModal({ action: 'cancel', title: 'Cancel Permit', placeholder: 'Mandatory reason for cancellation…', danger: true })} className="omx-btn omx-btn--danger">
                <XCircle size={13} />Cancel
              </button>
            )}
          </div>
          {actionLoading && (
            <p className="omx-actionbar__note" style={{ marginTop: 10 }}>
              <RefreshCw size={13} className="animate-spin" style={{ color: 'var(--om-blue)' }} />Processing…
            </p>
          )}
        </div>
      </motion.div>
    </div>

    {/* Reason modal (suspend / cancel) */}
    <AnimatePresence>
      {reasonModal && (
        <ReasonModal
          title={reasonModal.title}
          placeholder={reasonModal.placeholder}
          dangerMode={reasonModal.danger}
          confirmLabel={reasonModal.action === 'cancel' ? 'Cancel Permit' : reasonModal.action === 'spApproveJHA_query' ? 'Submit Query' : reasonModal.action === 'spApproveJHA_reject' ? 'Reject JHA' : 'Confirm Suspension'}
          loading={!!actionLoading}
          onCancel={() => setReasonModal(null)}
          onConfirm={async (reason) => {
            const action = reasonModal!.action;
            if (action === 'spApproveJHA_query') {
              await onAction('spApproveJHA', permit.id, { signature_text: sigText, notes: reason, decision: 'query' });
            } else if (action === 'spApproveJHA_reject') {
              await onAction('spApproveJHA', permit.id, { signature_text: sigText, notes: reason, decision: 'reject' });
            } else {
              await onAction(action, permit.id, reason);
            }
            setReasonModal(null);
          }}
        />
      )}
    </AnimatePresence>
    </>
  );
};

const PermitCard: React.FC<{ permit: PTWPermit; currentUser: CurrentUser | null; onClick: () => void }> = ({ permit, currentUser, onClick }) => {
  const wt = WORK_TYPE_CONFIG[permit.work_type] || WORK_TYPE_CONFIG.general;
  const WorkTypeIcon = wt.icon;
  const myRole = getMyRoleOnPermit(permit, currentUser);
  const myAllowedActions = ROLE_STATUS_ACTIONS[myRole]?.[permit.status] || [];
  const isMyTurnOnCard = myAllowedActions.length > 0;
  const whenLabel = permit.planned_start
    ? new Date(permit.planned_start).toLocaleDateString()
    : new Date(permit.created_at).toLocaleDateString();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`omx-permit${isMyTurnOnCard ? ' is-actionable' : ''}`}
      aria-label={`Open permit ${permit.permit_no}`}
    >
      <div className="omx-permit__top">
        <div className="omx-permit__ident">
          <span className="omx-permit__mark"><WorkTypeIcon size={16} /></span>
          <div className="min-w-0">
            <p className="omx-permit__no">{permit.permit_no}</p>
            <p className="omx-permit__type">{wt.label}</p>
          </div>
        </div>
        <div className="omx-permit__badges">
          {permit.is_hv_involved && <span className="omx-pill omx-pill--err">HV</span>}
          <StatusBadge status={permit.status} />
        </div>
      </div>

      {myRole !== 'VIEWER' && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`omx-role ${ROLE_ON_PERMIT_LABELS[myRole]?.badgeCls}`}>
            <User size={10} />You: {ROLE_ON_PERMIT_LABELS[myRole]?.label}
          </span>
          {isMyTurnOnCard && <span className="omx-permit__cue">Action needed</span>}
        </div>
      )}

      <p className="omx-permit__desc">{permit.work_description || 'No work description recorded'}</p>

      {permit.sup_permit_types && permit.sup_permit_types.length > 0 && (
        <div className="omx-permit__tags">
          {permit.sup_permit_types.map(t => (
            <span key={t} className="omx-permit__tag">{t.replace('_', ' ')}</span>
          ))}
        </div>
      )}

      <div className="omx-permit__foot">
        <div className="omx-permit__facts">
          {permit.equipment_kks && <span className="omx-permit__kks">{permit.equipment_kks}</span>}
          {permit.pr_name && (
            <span className="omx-permit__fact"><User size={11} /><span>{permit.pr_name}</span></span>
          )}
        </div>
        <span className="omx-permit__date"><Calendar size={11} />{whenLabel}</span>
      </div>
    </div>
  );
};

const PermitRow: React.FC<{ permit: PTWPermit; onClick: () => void }> = ({ permit, onClick }) => {
  const wt = WORK_TYPE_CONFIG[permit.work_type] || WORK_TYPE_CONFIG.general;
  const WorkTypeIcon = wt.icon;
  const whenLabel = permit.planned_start
    ? new Date(permit.planned_start).toLocaleDateString()
    : new Date(permit.created_at).toLocaleDateString();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className="omx-permitrow"
      aria-label={`Open permit ${permit.permit_no}`}
    >
      <div className="omx-permit__ident">
        <span className="omx-permit__mark" style={{ width: 28, height: 28 }}><WorkTypeIcon size={14} /></span>
        <div className="min-w-0">
          <p className="omx-permit__no">{permit.permit_no}</p>
          <p className="omx-permit__type">{wt.label}</p>
        </div>
      </div>

      <StatusBadge status={permit.status} />

      <p className="omx-permitrow__desc">{permit.work_description || 'No work description recorded'}</p>

      <span className="omx-permit__date omx-permitrow__date"><Calendar size={11} />{whenLabel}</span>
    </div>
  );
};

const KPIPanel: React.FC<{ siteCode: string; viewMode: 'card' | 'list'; onViewModeChange: (m: 'card' | 'list') => void }> = ({ siteCode, viewMode, onViewModeChange }) => {
  const [kpi, setKpi] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { ptwKPIApi.summary(siteCode).then(setKpi).catch(() => setKpi(null)).finally(() => setLoading(false)); }, [siteCode]);

  const viewToggle = (
    <div className="omx-seg" role="group" aria-label="Permit layout">
      <button type="button" className="omx-seg__btn" aria-pressed={viewMode === 'card'} aria-label="Card view" onClick={() => onViewModeChange('card')}>
        <LayoutGrid size={13} />
      </button>
      <button type="button" className="omx-seg__btn" aria-pressed={viewMode === 'list'} aria-label="List view" onClick={() => onViewModeChange('list')}>
        <List size={13} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="omx-card mb-4">
        <div className="omx-card__head">
          <span className="omx-card__title"><BarChart2 size={15} />Automation KPIs</span>
          {viewToggle}
        </div>
        <div className="omx-card__body"><div className="omx-skel" style={{ height: 86 }} /></div>
      </div>
    );
  }
  if (!kpi) return null;

  const avgIssuanceValue = kpi.avg_issuance_mins !== null && kpi.avg_issuance_mins !== undefined
    ? `${kpi.avg_issuance_mins}`
    : 'Not issued yet';
  const avgIssuanceUnit = kpi.avg_issuance_mins !== null && kpi.avg_issuance_mins !== undefined ? 'min' : '';

  const kpiCards: { label: string; val: React.ReactNode; unit?: string; note?: string; sub: string }[] = [
    {
      label: 'Avg Issuance',
      val: avgIssuanceValue,
      unit: avgIssuanceUnit,
      note: `Target ${kpi.target_issuance || '< 15 min'}`,
      sub: `Baseline: ${kpi.manual_baseline_issuance || '120-240 min'}`,
    },
    { label: 'Total PTWs',  val: kpi.total,                 sub: 'All time' },
    { label: 'Pending',     val: kpi.pending,               sub: 'Awaiting action' },
    { label: 'Active',      val: kpi.active,                sub: 'Right now' },
    { label: 'Completed',   val: kpi.completed_this_month,  sub: 'This month' },
    { label: 'Cancelled',   val: kpi.cancelled,             sub: 'All time' },
  ];

  return (
    <div className="omx-card mb-4">
      <div className="omx-card__head">
        <span className="omx-card__title"><BarChart2 size={15} />Automation KPIs</span>
        {viewToggle}
      </div>
      <div className="omx-card__body omx-card__body--flush">
        <div className="omx-kpis" style={{ border: 0, borderRadius: 0 }}>
          {kpiCards.map(item => (
            <div key={item.label} className="omx-kpi">
              <span className="omx-kpi__label">{item.label}</span>
              <span className="omx-kpi__value">
                {item.val}
                {item.unit ? <span className="omx-kpi__unit">{item.unit}</span> : null}
              </span>
              {item.note && <p className="omx-kpi__note">{item.note}</p>}
              <p className="omx-kpi__sub">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PTWPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const navigate = useNavigate();
  const [permits, setPermits] = useState<PTWPermit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState<'all' | 'mine' | 'active' | 'pending' | 'history'>(() => {
    try {
      const stored = localStorage.getItem('ptw_active_tab');
      if (stored === 'all' || stored === 'mine' || stored === 'active' || stored === 'pending' || stored === 'history') {
        return stored as 'all' | 'mine' | 'active' | 'pending' | 'history';
      }
    } catch (e) {}
    return 'all';
  });

  useEffect(() => {
    try {
      localStorage.setItem('ptw_active_tab', activeTab);
    } catch (e) {}
  }, [activeTab]);

  const [selectedPermit, setSelectedPermit] = useState<PTWPermit | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const siteCode = site || '';
  const siteDisplayName = useSiteDisplayName(siteCode);

  // Load current user from session + API
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedId = sessionStorage.getItem('user_id') || localStorage.getItem('user_id');
        const storedRole = sessionStorage.getItem('user_role') || localStorage.getItem('user_role') || '';
        const storedName = sessionStorage.getItem('user_name') || localStorage.getItem('user_name') || '';
        const storedUsername = sessionStorage.getItem('username') || localStorage.getItem('username') || '';
        // Quick local seed — shows role immediately, API result refines it
        if (storedId) {
          setCurrentUser({ id: parseInt(storedId, 10), name: storedName, username: storedUsername, role: storedRole });
        }
        // Fetch authoritative profile from API
        const r = await backendApiService.getCurrentUser();
        if (r?.success && r?.user) {
          const u = r.user;
          setCurrentUser({
            id: u.id,
            name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
            username: u.username || u.email || String(u.id),
            role: u.role_name || u.role || storedRole,
          });
        }
      } catch { /* use whatever is in state already */ }
    };
    loadUser();
  }, []);

  const loadPermits = useCallback(async () => {
    setLoading(true); setError('');
    try { const d = await ptwApi.list(siteCode); setPermits(Array.isArray(d) ? d : d?.results || []); }
    catch { setError('Failed to load permits.'); setPermits([]); }
    finally { setLoading(false); }
  }, [siteCode]);

  useEffect(() => { loadPermits(); ptwNotificationsApi.list().then((d: any) => setNotifications(Array.isArray(d) ? d : [])).catch(() => { }); }, [loadPermits]);

  const openPermitDetail = async (p: PTWPermit) => {
    try { const d = await ptwApi.detail(p.id); setSelectedPermit(d); }
    catch { setSelectedPermit(p); }
  };

  const handleAction = async (action: string, permitId: number, extra?: any): Promise<void> => {
    setActionLoading(`${action}_${permitId}`);
    try {
      switch (action) {
        case 'submitForReview': await ptwApi.submitForReview(permitId); break;
        case 'spApproveJHA': await ptwApi.spApproveJHA(permitId, extra || ''); break;
        case 'scPrepare': await ptwApi.scPrepare(permitId, extra || ''); break;
        case 'piIssue': await ptwApi.piIssue(permitId, extra || ''); break;
        case 'conductTBT': await ptwApi.conductTBT(permitId, extra || {}); break;
        case 'suspend': await ptwApi.suspend(permitId, extra || ''); break;
        case 'resume': await ptwApi.resume(permitId); break;
        case 'surrender': await ptwApi.surrender(permitId, extra || ''); break;
        case 'close': await ptwApi.close(permitId, extra || ''); break;
        case 'cancel': await ptwApi.cancel(permitId, extra || ''); break;
      }
      await loadPermits();
      if (selectedPermit?.id === permitId) { const d = await ptwApi.detail(permitId); setSelectedPermit(d); }
    } catch (e: any) { alert(e?.message || 'Action failed.'); }
    finally { setActionLoading(''); }
  };

  const filteredPermits = permits.filter(p => {
    const q = searchQuery.toLowerCase();
    const s = p.permit_no?.toLowerCase().includes(q) || (p.work_description || '').toLowerCase().includes(q) || (p.equipment_kks || '').toLowerCase().includes(q);
    const st = filterStatus === 'all' || p.status === filterStatus;
    const ty = filterType === 'all' || p.work_type === filterType;
    if (activeTab === 'mine') {
      const uid = currentUser?.id;
      const isMine = uid && (
        p.safety_controller_id === uid || p.permit_issuer_id === uid ||
        p.permit_receiver_id === uid || p.isolation_authority_id === uid || p.skilled_person_id === uid
      );
      return s && ty && !!isMine;
    }
    if (activeTab === 'active') return s && ty && p.status === 'active';
    if (activeTab === 'pending') return s && ty && (p.status === 'draft' || p.status === 'jha_review' || p.status === 'prepared' || p.status === 'issued' || p.status === 'tbt_pending');
    if (activeTab === 'history') return s && ty && (p.status === 'completed' || p.status === 'cancelled' || p.status === 'surrendered');
    return s && st && ty;
  }).sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return bTime - aTime;
    return (b.id || 0) - (a.id || 0);
  });

  // Count permits where current user has a pending action (for Mine tab badge)
  const myActionCount = permits.filter(p => {
    const uid = currentUser?.id;
    if (!uid) return false;
    const myRole = getMyRoleOnPermit(p, currentUser);
    return (ROLE_STATUS_ACTIONS[myRole]?.[p.status] || []).length > 0;
  }).length;

  const unreadNotifs = notifications.filter(n => !n.is_read).length;

  return (
    <div
      className="om-root omx flex flex-col min-h-screen"
      style={{
        background: '#F6F8FB',
        fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
        paddingTop: '64px',
      }}
    >
      <div className="flex-1 w-full min-w-0 transition-all duration-300">
        <div
          style={{
            position: 'sticky',
            top: '64px',
            zIndex: 20,
            background: '#FFFFFF',
            borderBottom: '1px solid #DFE6EE',
          }}
        >
          <div className="omx-shell" style={{ paddingLeft: '28px', paddingRight: '28px' }}>
            <div
              className="omx-pagehead"
              style={{
                marginBottom: 0,
                borderBottom: 0,
                paddingTop: '18px',
                paddingBottom: '14px',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="omx-crumbs">
                  <span>Operations &amp; Maintenance</span>
                  <ChevronRight />
                  <b>Permit to Work</b>
                </div>
                <span className="om-eyebrow">{siteDisplayName} &middot; Work Authorisation</span>
                <h1 className="om-h1" style={{ marginTop: 8 }}>Permit to Work</h1>
              </div>

              <div className="omx-pagehead__actions">
                <button
                  onClick={() => setShowNotifs(!showNotifs)}
                  className="omx-btn"
                  style={{ position: 'relative', padding: '8px 10px' }}
                  aria-label={`Notifications${unreadNotifs > 0 ? ` (${unreadNotifs} unread)` : ''}`}
                >
                  <Bell size={15} strokeWidth={1.9} />
                  {unreadNotifs > 0 && (
                    <span
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        minWidth: 16, height: 16, padding: '0 4px',
                        borderRadius: 8, background: '#B3261E', color: '#fff',
                        fontSize: 10, fontWeight: 600,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {unreadNotifs}
                    </span>
                  )}
                </button>

                <button
                  onClick={loadPermits}
                  className="omx-btn"
                  style={{ padding: '8px 10px' }}
                  title="Refresh permits"
                  aria-label="Refresh permits"
                >
                  <RefreshCw size={15} strokeWidth={1.9} className={loading ? 'animate-spin' : ''} />
                </button>

                {/* Export has no implementation yet: disabled with an explanation rather than a dead click */}
                <button
                  type="button"
                  className="omx-btn"
                  disabled
                  title="Permit export is not available yet" aria-label="Permit export is not available yet"
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  <Download size={14} strokeWidth={1.9} /> Export
                </button>

                <button onClick={() => setShowCreateWizard(true)} className="omx-btn omx-btn--primary">
                  <Plus size={14} strokeWidth={2} /> New PTW
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="omx-shell px-4 sm:px-6 md:px-8 py-6 space-y-4" style={{ paddingLeft: '28px', paddingRight: '28px' }}>
          <AnimatePresence>
            {showNotifs && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                role="dialog" aria-label="Permit notifications"
                className="omx-panel fixed top-24 right-6 w-[21rem] z-50 max-h-96 overflow-y-auto">
                <div className="omx-panel__head">
                  <span className="omx-card__title"><Bell size={14} />Notifications</span>
                  <button type="button" onClick={() => setShowNotifs(false)} className="omx-modal__close" aria-label="Close notifications"><X size={15} /></button>
                </div>
                {notifications.length === 0 ? (
                  <div className="omx-state" style={{ padding: '30px 20px' }}>
                    <p className="omx-state__text">You have no notifications on this site.</p>
                  </div>
                ) : notifications.map(n => (
                  <div key={n.id} className={`omx-panel__item${!n.is_read ? ' is-unread' : ''}`}>
                    <p className="omx-panel__title">{n.title}</p>
                    <p className="omx-panel__text">{n.message}</p>
                    <p className="omx-panel__time">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          <KPIPanel siteCode={siteCode} viewMode={viewMode} onViewModeChange={setViewMode} />
          <div className="omx-card mb-4">
            <div className="omx-card__head">
              <div className="omx-seg" role="tablist" aria-label="Permit filter">
                {(['all', 'mine', 'active', 'pending', 'history'] as const).map(tab => (
                  <button key={tab} type="button" role="tab" aria-pressed={activeTab === tab} aria-selected={activeTab === tab}
                    onClick={() => setActiveTab(tab)} className="omx-seg__btn">
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {tab === 'mine' && myActionCount > 0 && (
                      <span className="omx-seg__count">{myActionCount}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="omx-toolbar">
                <label className="omx-field omx-field--search">
                  <Search size={14} className="flex-shrink-0" style={{ color: 'var(--om-subtle)' }} />
                  <input type="text" placeholder="Search permits…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} aria-label="Search permits" />
                </label>
                <label className="omx-field">
                  <select value={filterType} onChange={e => setFilterType(e.target.value)} aria-label="Filter by work type">
                    <option value="all">All Types</option>
                    {Object.entries(WORK_TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </label>
                {activeTab === 'all' && (
                  <label className="omx-field">
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} aria-label="Filter by status">
                      <option value="all">All Status</option>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </label>
                )}
              </div>
            </div>
            <div className="omx-card__body">
              {loading && (
                <div className="omx-permits">
                  {[1, 2, 3].map(i => <div key={i} className="omx-skel" />)}
                </div>
              )}
              {!loading && error && (
                <div className="omx-notice omx-notice--warn"><AlertCircle size={15} /><span>{error}</span></div>
              )}
              {!loading && !error && filteredPermits.length === 0 && (
                <div className="omx-state">
                  <span className="omx-state__icon"><FileWarning size={22} /></span>
                  <p className="omx-state__title">
                    {activeTab === 'mine' ? 'Nothing awaiting you' : 'No permits match this view'}
                  </p>
                  <p className="omx-state__text">
                    {activeTab === 'mine'
                      ? 'No permits on this site currently list you as Permit Issuer, Receiver, Isolation Authority or Skilled Person.'
                      : 'Adjust the filters above, or raise a new permit to get work authorised.'}
                  </p>
                  {activeTab !== 'mine' && (
                    <button type="button" onClick={() => setShowCreateWizard(true)} className="omx-btn omx-btn--primary" style={{ marginTop: 4 }}>
                      <Plus size={14} strokeWidth={2} /> Create first PTW
                    </button>
                  )}
                </div>
              )}
              {!loading && filteredPermits.length > 0 && (
                <div className={viewMode === 'card' ? 'omx-permits' : 'omx-permits omx-permits--rows'}>
                  {filteredPermits.map(p => viewMode === 'card' ? (
                    <PermitCard key={p.id} permit={p} currentUser={currentUser} onClick={() => openPermitDetail(p)} />
                  ) : (
                    <PermitRow key={p.id} permit={p} onClick={() => openPermitDetail(p)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showCreateWizard && <CreatePTWWizard site={siteCode} onClose={() => setShowCreateWizard(false)} onCreated={(p: any) => { setPermits(prev => [p, ...prev]); setShowCreateWizard(false); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {selectedPermit && <PermitDetail permit={selectedPermit} currentUser={currentUser} onClose={() => setSelectedPermit(null)} onAction={handleAction} actionLoading={actionLoading} />}
      </AnimatePresence>
    </div>
  );
};

export default PTWPage;
